/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Layers, Star, DownloadCloud, FileText, Link, Calendar, Landmark, Save, Eye, CheckCircle2, Lock, Lightbulb, AlertCircle, FileQuestion, Maximize2, Minimize2, ZoomIn, ZoomOut, Trash2, Clock, X, FolderOpen, PanelLeft, Search, Sparkles, ChevronUp, ChevronDown } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../ReduxToolkit/Store';
import { toggleFavorite as toggleFavoriteAction, fetchFavorites } from '../../ReduxToolkit/FavoriteSlice';
import { fetchFolders } from '../../ReduxToolkit/FolderSlice';
import { LegalDocument, UserNote } from '../../types';
import { noteService } from '../../Services/NoteService';
import { useAuth } from '../../Hooks/useAuth';
import { usePermissions } from '../../Hooks/usePermissions';
import { StatusBadge } from '../ui/StatusBadge';
import { LegalOriginTag } from '../ui/LegalOriginTag';
import { FolderModal } from '../ui/FolderModal';
import { formatFrenchDate, formatDomain, formatNature } from '../../utils/legalUtils';
import { 
  reconstructPdfText, 
  extractPageTextWithOcr, 
  findMatchesInPageText, 
  TextSearchResult, 
  loadTesseract,
  highlightTextWithMark,
  createAccentInsensitiveRegex,
  OcrWordBox
} from '../../utils/ocrSearchEngine';
import { documentService } from '../../Services/DocumentService';
import { downloadService } from '../../Services/DownloadService';
import apiClient from '../../Api/ApiClient';
import { useAuthModal } from '../../contexts/AuthModalContext';
import { Document, Page, pdfjs } from 'react-pdf';
import { LruPageCache } from '../../utils/LruPageCache';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Configurer le worker pour react-pdf (obligatoire)
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface VirtualPageItemProps {
  pageNumber: number;
  documentId: string;
  pdfZoom: number;
  isNearViewport: boolean;
  isScrollingFast?: boolean;
  pageDataCache: React.MutableRefObject<LruPageCache>;
  activeSearchTerm?: string;
  ocrData?: {
    text: string;
    isOcr: boolean;
    words?: OcrWordBox[];
    canvasWidth?: number;
    canvasHeight?: number;
  };
}

function extractWordsFromPage(p: any): OcrWordBox[] {
  const result: OcrWordBox[] = [];
  if (!p) return result;

  const pushWord = (w: any) => {
    if (!w || !w.text) return;
    const txt = String(w.text).trim();
    if (!txt) return;

    let x0 = 0, y0 = 0, x1 = 0, y1 = 0;
    if (Array.isArray(w.bbox) && w.bbox.length >= 4) {
      x0 = Number(w.bbox[0]);
      y0 = Number(w.bbox[1]);
      x1 = Number(w.bbox[2]);
      y1 = Number(w.bbox[3]);
    } else if (w.bbox && typeof w.bbox === 'object') {
      x0 = Number(w.bbox.x0 ?? 0);
      y0 = Number(w.bbox.y0 ?? 0);
      x1 = Number(w.bbox.x1 ?? 0);
      y1 = Number(w.bbox.y1 ?? 0);
    }
    result.push({ text: txt, bbox: { x0, y0, x1, y1 } });
  };

  if (Array.isArray(p.words) && p.words.length > 0) {
    for (const w of p.words) pushWord(w);
  } else if (Array.isArray(p.lines)) {
    for (const line of p.lines) {
      if (Array.isArray(line?.words)) {
        for (const w of line.words) pushWord(w);
      }
    }
  }

  return result;
}

const VirtualPageItem: React.FC<VirtualPageItemProps> = ({
  pageNumber,
  documentId,
  pdfZoom,
  isNearViewport,
  isScrollingFast,
  pageDataCache,
  activeSearchTerm,
  ocrData,
}) => {
  const [pageData, setPageData] = React.useState<Uint8Array | null>(() => {
    const cached = pageDataCache.current.get(pageNumber);
    return cached && cached.byteLength > 0 ? cached : null;
  });
  const [hasError, setHasError] = React.useState<boolean>(false);
  const [reloadKey, setReloadKey] = React.useState<number>(0);

  const customTextRenderer = React.useCallback(
    ({ str }: { str: string }) => {
      if (!activeSearchTerm || !str) return str;
      return highlightTextWithMark(str, activeSearchTerm);
    },
    [activeSearchTerm]
  );

  const matchingOcrBoxes = React.useMemo(() => {
    if (!ocrData || !ocrData.words || !ocrData.words.length || !ocrData.canvasWidth || !activeSearchTerm) {
      return [];
    }
    const cleanTerm = activeSearchTerm.trim();
    if (!cleanTerm) return [];

    const regex = createAccentInsensitiveRegex(cleanTerm);
    if (!regex) return [];

    return ocrData.words.filter(w => {
      if (!w.text || !w.text.trim()) return false;
      regex.lastIndex = 0;
      return regex.test(w.text.trim());
    });
  }, [ocrData, activeSearchTerm]);

  const scale = pdfZoom / 100;
  const pageWidth = Math.round(595 * scale);
  const pageHeight = Math.round(842 * scale);

  React.useEffect(() => {
    // 1. Si déjà présent dans notre cache mémoire LRU : affichage instantané (0 ms)
    const cached = pageDataCache.current.get(pageNumber);
    if (cached && cached.byteLength > 0) {
      setPageData(cached);
      setHasError(false);
      return;
    }

    // 2. Si loin du viewport OU défilement rapide en cours : ne pas déclencher de requête réseau inutile
    if (!isNearViewport || isScrollingFast) {
      return;
    }

    const abortController = new AbortController();
    let isMounted = true;
    setHasError(false);

    apiClient.getArrayBuffer(
      `/api/documents/${documentId}/pages/${pageNumber}`,
      { signal: abortController.signal }
    )
      .then(bytes => {
        if (!isMounted) return;
        pageDataCache.current.set(pageNumber, bytes);
        setPageData(bytes);
      })
      .catch(err => {
        if (!isMounted || abortController.signal.aborted || err?.name === 'AbortError') {
          return;
        }
        console.error(`Erreur chargement page ${pageNumber}:`, err);
        setHasError(true);
      });

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [pageNumber, documentId, isNearViewport, isScrollingFast, reloadKey]);

  // CRITIQUE : Générer un NOUVEL ArrayBuffer physique copié via .buffer.slice()
  // Chaque fois qu'une page redevient visible (ex: saut depuis page 150 vers page 1),
  // le Web Worker de react-pdf reçoit un buffer vierge, empêchant le détachement de la source en mémoire !
  const fileProp = React.useMemo(() => {
    if (!isNearViewport) return null;
    const current = (pageData && pageData.byteLength > 0) ? pageData : pageDataCache.current.get(pageNumber);
    if (!current || current.byteLength === 0) return null;
    const bufferCopy = current.buffer.slice(current.byteOffset, current.byteOffset + current.byteLength);
    return { data: new Uint8Array(bufferCopy) };
  }, [isNearViewport, pageData, pageNumber, reloadKey]);

  // Si trop loin du viewport ou en défilement ultra-rapide sans cache, afficher le placeholder léger
  if (!isNearViewport || (isScrollingFast && !fileProp)) {
    return (
      <div
        style={{ width: `${pageWidth}px`, height: `${pageHeight}px` }}
        className="bg-white/90 border border-zinc-200/80 shadow-md rounded-sm flex flex-col items-center justify-center font-mono text-xs select-none text-zinc-400 gap-1.5"
      >
        <FileText className="w-8 h-8 opacity-30" />
        <span>Page {pageNumber}</span>
      </div>
    );
  }

  if (hasError) {
    return (
      <div
        style={{ width: `${pageWidth}px`, height: `${pageHeight}px` }}
        className="bg-zinc-50 border border-zinc-200 shadow-md rounded-sm flex flex-col items-center justify-center text-zinc-500 p-4 select-none"
      >
        <FileText className="w-8 h-8 opacity-40 mb-2 text-zinc-400" />
        <span className="text-xs font-semibold text-zinc-700">Page {pageNumber} temporairement indisponible</span>
        <button
          type="button"
          onClick={() => {
            pageDataCache.current.delete(pageNumber);
            setPageData(null);
            setReloadKey(k => k + 1);
          }}
          className="mt-2 text-xs font-bold px-3 py-1 bg-white border border-zinc-300 rounded-lg hover:bg-zinc-100 text-zinc-800 transition cursor-pointer shadow-2xs"
        >
          Recharger
        </button>
      </div>
    );
  }

  return (
    <div
      style={{ minWidth: `${pageWidth}px`, minHeight: `${pageHeight}px` }}
      className="flex justify-center select-none"
      onContextMenu={(e) => e.preventDefault()}
    >
      {fileProp ? (
        <Document
          file={fileProp}
          onLoadSuccess={() => setHasError(false)}
          onLoadError={(err) => {
            console.warn(`Erreur de rendu react-pdf pour la page ${pageNumber}:`, err);
            pageDataCache.current.delete(pageNumber);
            setPageData(null);
            setReloadKey(k => k + 1);
          }}
          loading={
            <div
              style={{ width: `${pageWidth}px`, height: `${pageHeight}px` }}
              className="bg-white border border-zinc-200 text-zinc-400 shadow-lg rounded-sm flex flex-col items-center justify-center animate-pulse select-none"
            >
              <FileText className="w-8 h-8 opacity-40 mb-2 animate-bounce" />
              <span className="text-xs font-semibold">Chargement de la page {pageNumber}...</span>
            </div>
          }
          error={
            <div
              style={{ width: `${pageWidth}px`, height: `${pageHeight}px` }}
              className="bg-zinc-50 border border-zinc-200 shadow-md rounded-sm flex flex-col items-center justify-center text-zinc-500 p-4 select-none"
            >
              <FileText className="w-8 h-8 opacity-40 mb-2 text-zinc-400" />
              <span className="text-xs font-semibold text-zinc-700">Page {pageNumber}</span>
              <button
                type="button"
                onClick={() => {
                  pageDataCache.current.delete(pageNumber);
                  setPageData(null);
                  setReloadKey(k => k + 1);
                }}
                className="mt-2 text-xs font-bold px-3 py-1 bg-white border border-zinc-300 rounded-lg hover:bg-zinc-100 text-zinc-800 transition cursor-pointer shadow-2xs"
              >
                Recharger
              </button>
            </div>
          }
        >
          <div className="relative inline-block select-none" onContextMenu={(e) => e.preventDefault()}>
            <Page
              key={`page_${pageNumber}_${activeSearchTerm || 'none'}`}
              pageNumber={1}
              scale={scale}
              renderTextLayer={true}
              renderAnnotationLayer={false}
              customTextRenderer={customTextRenderer}
              className="shadow-2xl select-none pointer-events-none rounded-sm transition-all"
            />
            {/* Surlignage jaune Docarn AI (OCR) pour documents scannés */}
            {matchingOcrBoxes.length > 0 && ocrData?.canvasWidth && ocrData?.canvasHeight && (
              <div className="absolute inset-0 pointer-events-none z-10 overflow-hidden">
                {matchingOcrBoxes.map((w, idx) => (
                  <div
                    key={`ocr_hl_${pageNumber}_${idx}`}
                    className="docarn-ocr-highlight-box"
                    style={{
                      left: `${(w.bbox.x0 / ocrData.canvasWidth!) * 100}%`,
                      top: `${(w.bbox.y0 / ocrData.canvasHeight!) * 100}%`,
                      width: `${((w.bbox.x1 - w.bbox.x0) / ocrData.canvasWidth!) * 100}%`,
                      height: `${((w.bbox.y1 - w.bbox.y0) / ocrData.canvasHeight!) * 100}%`,
                    }}
                    title={w.text}
                  />
                ))}
              </div>
            )}
          </div>
        </Document>
      ) : (
        <div
          style={{ width: `${pageWidth}px`, height: `${pageHeight}px` }}
          className="bg-white border border-zinc-200 text-zinc-400 shadow-lg rounded-sm flex flex-col items-center justify-center animate-pulse select-none"
        >
          <FileText className="w-8 h-8 opacity-40 mb-2 animate-bounce" />
          <span className="text-xs font-semibold">Chargement de la page {pageNumber}...</span>
        </div>
      )}
    </div>
  );
};

interface ThumbnailPageItemProps {
  pageNumber: number;
  documentId: string;
  isActive: boolean;
  onClick: () => void;
  pageDataCache: React.MutableRefObject<LruPageCache>;
}

const ThumbnailPageItem: React.FC<ThumbnailPageItemProps> = ({
  pageNumber,
  documentId,
  isActive,
  onClick,
  pageDataCache,
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = React.useState(false);
  const [pageData, setPageData] = React.useState<Uint8Array | null>(() => {
    const cached = pageDataCache.current.get(pageNumber);
    return cached && cached.byteLength > 0 ? cached : null;
  });
  const [isLoading, setIsLoading] = React.useState(false);

  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    // Ne charger et rendre que les vignettes visibles dans le volet pour préserver la mémoire
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          setIsVisible(true);
        } else {
          setIsVisible(false);
        }
      },
      { rootMargin: '200px 0px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    if (!isVisible) return;

    const cached = pageDataCache.current.get(pageNumber);
    if (cached && cached.byteLength > 0) {
      setPageData(cached);
      return;
    }

    const abortController = new AbortController();
    let isMounted = true;
    setIsLoading(true);

    apiClient.getArrayBuffer(
      `/api/documents/${documentId}/pages/${pageNumber}`,
      { signal: abortController.signal }
    )
      .then((bytes) => {
        if (!isMounted) return;
        pageDataCache.current.set(pageNumber, bytes);
        setPageData(bytes);
        setIsLoading(false);
      })
      .catch((err) => {
        if (!isMounted || abortController.signal.aborted || err?.name === 'AbortError') return;
        console.error(`Erreur chargement vignette ${pageNumber}:`, err);
        setIsLoading(false);
      });

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [isVisible, documentId, pageNumber, pageDataCache]);

  const fileProp = React.useMemo(() => {
    if (!isVisible) return null;
    const current = (pageData && pageData.byteLength > 0) ? pageData : pageDataCache.current.get(pageNumber);
    if (!current || current.byteLength === 0) return null;
    const bufferCopy = current.buffer.slice(current.byteOffset, current.byteOffset + current.byteLength);
    return { data: new Uint8Array(bufferCopy) };
  }, [isVisible, pageData, pageNumber]);

  return (
    <div
      ref={containerRef}
      onClick={onClick}
      className={`w-full p-2 rounded-xl border transition-all cursor-pointer flex flex-col items-center group select-none ${
        isActive
          ? 'border-primary-750 ring-2 ring-primary-500/25 bg-primary-50/80 shadow-sm'
          : 'border-zinc-200/90 bg-white hover:border-zinc-300 hover:bg-zinc-50 shadow-2xs'
      }`}
    >
      <div className="w-full aspect-[1/1.414] rounded-md overflow-hidden bg-zinc-100 border border-zinc-200/80 flex items-center justify-center relative shadow-2xs">
        {fileProp ? (
          <Document
            file={fileProp}
            loading={
              <div className="w-full h-full flex flex-col items-center justify-center bg-zinc-50 animate-pulse text-zinc-300">
                <FileText className="w-5 h-5 opacity-40 animate-bounce" />
                <span className="text-[9px] font-mono mt-1 text-zinc-400">P. {pageNumber}</span>
              </div>
            }
            error={
              <div className="w-full h-full flex items-center justify-center bg-red-50 text-[10px] text-red-400 font-mono">
                P. {pageNumber}
              </div>
            }
          >
            <Page
              pageNumber={1}
              width={140}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              className="w-full h-auto pointer-events-none select-none"
            />
          </Document>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center bg-zinc-50 text-zinc-400">
            {isLoading ? (
              <div className="flex flex-col items-center gap-1">
                <span className="w-4 h-4 rounded-full border-2 border-primary-300 border-t-primary-750 animate-spin" />
                <span className="text-[9px] font-mono text-zinc-400">{pageNumber}</span>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-1 opacity-50">
                <FileText className="w-5 h-5 text-zinc-300" />
                <span className="text-[10px] font-mono font-bold text-zinc-400">{pageNumber}</span>
              </div>
            )}
          </div>
        )}

        {isActive && (
          <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-primary-750 shadow-xs ring-2 ring-white" />
        )}
      </div>

      <div className="flex items-center justify-between w-full px-1 mt-1.5">
        <span
          className={`text-[10px] font-mono font-bold transition-colors ${
            isActive ? 'text-primary-750 font-black' : 'text-zinc-500 group-hover:text-zinc-800'
          }`}
        >
          Page {pageNumber}
        </span>
        {isActive && (
          <span className="text-[9px] font-bold text-primary-750 bg-primary-100/70 px-1.5 py-0.2 rounded-full">
            Active
          </span>
        )}
      </div>
    </div>
  );
};

class PdfViewerErrorBoundary extends React.Component<
  { children: React.ReactNode; onReset?: () => void },
  { hasError: boolean }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: any, info: any) {
    console.warn("PdfViewerErrorBoundary a intercepté une exception :", error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="w-full h-full flex flex-col items-center justify-center p-8 bg-zinc-50 text-zinc-600 space-y-4 select-none">
          <div className="bg-amber-50 p-4 rounded-full border border-amber-200 shadow-sm">
            <AlertCircle className="w-8 h-8 text-amber-600" />
          </div>
          <h3 className="font-bold text-zinc-800 text-sm">Affichage réinitialisé suite au défilement</h3>
          <p className="text-xs text-zinc-500 max-w-md text-center leading-relaxed">
            Un tampon graphique temporaire a été réinitialisé pour protéger la mémoire de votre navigateur. Vos données et votre position sont intactes.
          </p>
          <button
            type="button"
            onClick={() => {
              this.setState({ hasError: false });
              this.props.onReset?.();
            }}
            className="px-4 py-2 bg-primary-750 hover:bg-primary-800 text-white rounded-xl text-xs font-bold transition shadow-xs cursor-pointer border-none"
          >
            Rafraîchir l'affichage
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

interface DocumentDetailViewProps {
  document: LegalDocument; // Might be null from router
  setView: (view: string) => void;
  setActiveDocumentId: (id: string | number | null) => void;
  allDocuments: LegalDocument[];
  favorites: (string | number)[];
  toggleFavorite: (id: string | number) => void;
  addDownload: (doc: LegalDocument) => void;
}

export const DocumentDetailView: React.FC<DocumentDetailViewProps> = ({
  document: _docProp,
  setView,
  setActiveDocumentId,
  allDocuments,
  favorites,
  toggleFavorite,
  addDownload
}) => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const { hasPermission } = usePermissions();
  const canDownload = hasPermission('download_doc');


  const [docData, setDocData] = React.useState<LegalDocument | null>(_docProp || location.state?.document || null);
  const [bestMatch] = React.useState<any>(location.state?.document?.bestMatch || null);
  const [loading, setLoading] = React.useState(!docData);

  React.useEffect(() => {
    if (!docData && id) {
      documentService.getDocumentById(id).then(res => {
        setDocData(res);
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, [id, docData]);

  const document = docData;

  const dispatch = useDispatch<AppDispatch>();
  const favoritesState = useSelector((state: RootState) => state.favorites.items);

  // Synchronise les favoris et dossiers du compte dès l'accès au document
  React.useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (authUser || token) {
      dispatch(fetchFavorites());
      dispatch(fetchFolders());
    }
  }, [authUser, dispatch]);

  const isFav = React.useMemo(() => {
    const activeId = document?.id || id;
    if (!activeId) return false;
    const docIdStr = String(activeId).toLowerCase().trim();
    const inRedux = Array.isArray(favoritesState) && favoritesState.some((f: any) => String(f.documentId).toLowerCase().trim() === docIdStr);
    const inProps = Array.isArray(favorites) && favorites.some((fid: any) => String(fid).toLowerCase().trim() === docIdStr);
    return Boolean(inRedux || inProps);
  }, [document?.id, id, favoritesState, favorites]);

  // Private notes state
  const [noteContent, setNoteContent] = React.useState('');
  const [documentNotes, setDocumentNotes] = React.useState<UserNote[]>([]);
  const [noteSavedFeedback, setNoteSavedFeedback] = React.useState(false);
  const [noteLoading, setNoteLoading] = React.useState(false);
  const [noteSaving, setNoteSaving] = React.useState(false);
  const [isConsultationLimitReached, setIsConsultationLimitReached] = React.useState(false);

  React.useEffect(() => {
    const handleLimit = () => setIsConsultationLimitReached(true);
    window.addEventListener('docarn:consultation-limit', handleLimit);
    return () => window.removeEventListener('docarn:consultation-limit', handleLimit);
  }, []);
  React.useEffect(() => {
    if (!document?.id || !authUser) {
      setNoteContent('');
      setDocumentNotes([]);
      return;
    }
    let isMounted = true;
    const loadNotes = async () => {
      setNoteLoading(true);
      try {
        const notes = await noteService.getNotesForDocument(document.id);
        if (isMounted) setDocumentNotes(notes || []);
      } catch (err) {
        console.error('Erreur de chargement des notes', err);
      } finally {
        if (isMounted) setNoteLoading(false);
      }
    };
    loadNotes();
    return () => { isMounted = false; };
  }, [document?.id, authUser]);

  // Elegant status message toast
  const [toastMessage, setToastMessage] = React.useState<string | null>(null);

  const triggerToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => {
      setToastMessage(null);
    }, 6000);
  };

  // Note state is loaded from the backend by the useEffect above.

  // Simulated PDF state
  const [pdfZoom, setPdfZoom] = React.useState(100);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [numPages, setNumPages] = React.useState<number | null>(null);
  const [pageInput, setPageInput] = React.useState<string>('1');

  React.useEffect(() => {
    setPageInput(String(currentPage));
  }, [currentPage]);

  const [viewMode, setViewMode] = React.useState<'single' | 'all'>('all');
  const viewerRef = React.useRef<HTMLDivElement>(null);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const pageContainerRefs = React.useRef<Map<number, HTMLDivElement>>(new Map());

  const scrollToPage = (p: number) => {
    const el = pageContainerRefs.current.get(p);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handlePageInputSubmit = () => {
    const parsed = parseInt(pageInput, 10);
    if (!isNaN(parsed) && parsed >= 1) {
      const clamped = numPages ? Math.min(numPages, Math.max(1, parsed)) : Math.max(1, parsed);
      setCurrentPage(clamped);
      setPageInput(String(clamped));
      if (viewMode === 'all') {
        scrollToPage(clamped);
      }
    } else {
      setPageInput(String(currentPage));
    }
  };

  const [isScrollingFast, setIsScrollingFast] = React.useState(false);
  const scrollStopTimerRef = React.useRef<NodeJS.Timeout | null>(null);
  const lastScrollTopRef = React.useRef<number>(0);
  const lastScrollTimeRef = React.useRef<number>(0);
  const scrollRafRef = React.useRef<number | null>(null);

  const computeClosestPage = React.useCallback(() => {
    if (!scrollContainerRef.current || viewMode !== 'all') return;
    const container = scrollContainerRef.current;
    const containerCenter = container.scrollTop + container.clientHeight / 3;

    // Calcul O(1) de la page estimée selon la hauteur moyenne pour éviter d'itérer sur 1000 éléments
    const scale = pdfZoom / 100;
    const itemHeight = Math.round(842 * scale) + 24; // hauteur page + gap-6 (24px)
    const estimated = Math.min(numPages || 1, Math.max(1, Math.round(container.scrollTop / itemHeight) + 1));

    let closestPage = estimated;
    let minDistance = Infinity;

    // Ne scanner qu'une courte fenêtre de ±4 pages autour de l'estimation
    const startP = Math.max(1, estimated - 4);
    const endP = Math.min(numPages || 1, estimated + 4);

    for (let p = startP; p <= endP; p++) {
      const el = pageContainerRefs.current.get(p);
      if (el) {
        const distance = Math.abs(containerCenter - el.offsetTop);
        if (distance < minDistance) {
          minDistance = distance;
          closestPage = p;
        }
      }
    }

    if (closestPage !== currentPage) {
      setCurrentPage(closestPage);
    }
  }, [viewMode, pdfZoom, numPages, currentPage]);

  const handleScroll = () => {
    if (!scrollContainerRef.current || viewMode !== 'all') return;
    const container = scrollContainerRef.current;
    const now = performance.now();
    const currentScrollTop = container.scrollTop;
    const deltaY = Math.abs(currentScrollTop - lastScrollTopRef.current);
    const deltaTime = Math.max(1, now - lastScrollTimeRef.current);
    const speed = deltaY / deltaTime; // pixels/ms

    lastScrollTopRef.current = currentScrollTop;
    lastScrollTimeRef.current = now;

    // Dès que la vitesse de défilement dépasse 1.2 px/ms, activer le mode rapide (suspension des requêtes)
    if (speed > 1.2 && !isScrollingFast) {
      setIsScrollingFast(true);
    }

    // Débouncing : 130ms après le dernier événement scroll, désactiver le mode rapide et fixer la page
    if (scrollStopTimerRef.current) {
      clearTimeout(scrollStopTimerRef.current);
    }
    scrollStopTimerRef.current = setTimeout(() => {
      setIsScrollingFast(false);
      computeClosestPage();
    }, 130);

    // Mettre à jour l'indicateur de page de façon fluide sans bloquer le rendu
    if (scrollRafRef.current === null) {
      scrollRafRef.current = requestAnimationFrame(() => {
        computeClosestPage();
        scrollRafRef.current = null;
      });
    }
  };

  const handleWheel = (e: React.WheelEvent) => {
    if (viewMode !== 'single') return;
    if (!scrollContainerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;

    if (e.deltaY > 60 && scrollTop + clientHeight >= scrollHeight - 10) {
      if (!numPages || currentPage < numPages) {
        setCurrentPage(p => p + 1);
        scrollContainerRef.current.scrollTop = 0;
      }
    } else if (e.deltaY < -60 && scrollTop <= 10) {
      if (currentPage > 1) {
        setCurrentPage(p => p - 1);
        scrollContainerRef.current.scrollTop = 0;
      }
    }
  };

  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const [isFolderModalOpen, setIsFolderModalOpen] = React.useState(false);
  const { openAuthModal } = useAuthModal();
  
  // Volet latéral multifonction (Miniatures & Recherche dans le texte)
  const [isSidebarOpen, setIsSidebarOpen] = React.useState<boolean>(true);
  const [sidebarTab, setSidebarTab] = React.useState<'thumbnails' | 'search'>('thumbnails');
  const thumbnailRefs = React.useRef<Map<number, HTMLDivElement>>(new Map());

  // Moteur de recherche hybride intelligent (Reconstruction vectorielle + OCR Tesseract côté client)
  const initialIncomingQuery = location.state?.searchQuery || '';
  const [searchQuery, setSearchQuery] = React.useState<string>(initialIncomingQuery);
  const [activeSearchTerm, setActiveSearchTerm] = React.useState<string>(initialIncomingQuery);
  const [searchResults, setSearchResults] = React.useState<TextSearchResult[]>([]);
  const [currentMatchIndex, setCurrentMatchIndex] = React.useState<number>(0);
  const [isSearching, setIsSearching] = React.useState<boolean>(false);
  const [searchProgress, setSearchProgress] = React.useState<{ 
    current: number; 
    total: number;
    phase?: 'digital' | 'ocr';
    ocrPercent?: number;
  } | null>(null);
  const [hasScannedPages, setHasScannedPages] = React.useState<boolean>(false);
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const searchAbortRef = React.useRef<boolean>(false);
  const extractedTextCache = React.useRef<Map<number, { 
    text: string; 
    isOcr: boolean;
    words?: OcrWordBox[];
    canvasWidth?: number;
    canvasHeight?: number;
  }>>(new Map());

  const singlePageCustomTextRenderer = React.useCallback(
    ({ str }: { str: string }) => {
      if (!activeSearchTerm || !str) return str;
      return highlightTextWithMark(str, activeSearchTerm);
    },
    [activeSearchTerm]
  );

  const [ocrVersion, setOcrVersion] = React.useState<number>(0);

  const currentPageOcrData = extractedTextCache.current.get(currentPage);
  const singlePageMatchingOcrBoxes = React.useMemo(() => {
    if (!currentPageOcrData || !currentPageOcrData.words || !currentPageOcrData.words.length || !currentPageOcrData.canvasWidth || !activeSearchTerm) {
      return [];
    }
    const cleanTerm = activeSearchTerm.trim();
    if (!cleanTerm) return [];

    const regex = createAccentInsensitiveRegex(cleanTerm);
    if (!regex) return [];

    return currentPageOcrData.words.filter(w => {
      if (!w.text || !w.text.trim()) return false;
      regex.lastIndex = 0;
      return regex.test(w.text.trim());
    });
  }, [currentPageOcrData, activeSearchTerm, ocrVersion]);

  // Raccourci clavier universel Ctrl+F / Cmd+F pour la recherche textuelle
  React.useEffect(() => {
    const handleSearchKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'f') {
        e.preventDefault();
        e.stopPropagation();
        if (!authUser) {
          openAuthModal();
          return;
        }
        setIsSidebarOpen(true);
        setSidebarTab('search');
        // Préchargement en arrière-plan du moteur OCR sans bloquer
        loadTesseract().catch(() => {});
        setTimeout(() => {
          searchInputRef.current?.focus();
          searchInputRef.current?.select();
        }, 100);
      }
    };
    window.addEventListener('keydown', handleSearchKey, { capture: true });
    return () => window.removeEventListener('keydown', handleSearchKey, { capture: true });
  }, [authUser]);

  const handleStartSearch = async (termOverride?: string, showToastOnEmpty: boolean = false) => {
    const query = (termOverride !== undefined ? termOverride : searchQuery).trim();
    if (!query || query.length < 2) {
      if (showToastOnEmpty) {
        triggerToast("Veuillez saisir au moins 2 caractères pour rechercher.");
      }
      return;
    }
    if (!document?.id || !numPages) return;

    searchAbortRef.current = false;
    setIsSearching(true);
    setActiveSearchTerm(query);
    setSearchResults([]);
    setCurrentMatchIndex(0);
    setSearchProgress({ current: 0, total: numPages, phase: 'digital' });

    const allMatches: TextSearchResult[] = [];
    let matchCounter = 0;

    // 0. Préchargement instantané du JSON OCR serveur (s'il existe déjà dans MinIO)
    if (extractedTextCache.current.size === 0) {
      try {
        const ocrData = await apiClient.get<any>(`/api/documents/${document.id}/ocr`);
        if (ocrData?.pages && Array.isArray(ocrData.pages)) {
          for (const p of ocrData.pages) {
            const pNum = p.pageNumber;
            const wordsList = extractWordsFromPage(p);

            extractedTextCache.current.set(pNum, {
              text: p.text || '',
              isOcr: true,
              words: wordsList,
              canvasWidth: p.width || 595,
              canvasHeight: p.height || 842,
            });
          }
          setHasScannedPages(true);
          setOcrVersion(v => v + 1);
        }
      } catch {
        // Document non encore traité par le pipeline OCR serveur, fallback sur le scan client
      }
    }

    for (let pageNum = 1; pageNum <= numPages; pageNum++) {
      if (searchAbortRef.current) break;
      setSearchProgress({ current: pageNum, total: numPages, phase: 'digital' });

      try {
        let cached = extractedTextCache.current.get(pageNum);
        let pageText = cached?.text || '';
        let isOcr = cached?.isOcr || false;

        if (!cached) {
          let bytes = pageDataCache.current.get(pageNum);
          if (!bytes || bytes.byteLength === 0) {
            bytes = await apiClient.getArrayBuffer(`/api/documents/${document.id}/pages/${pageNum}`);
            pageDataCache.current.set(pageNum, bytes);
          }
          const loadingTask = pdfjs.getDocument({ data: bytes.slice() });
          const pdfDoc = await loadingTask.promise;
          const page = await pdfDoc.getPage(1);

          // 1. Essai de reconstruction du texte natif PDF.js
          const textContent = await page.getTextContent();
          pageText = reconstructPdfText(textContent);

          // 2. Si aucun texte ou scan pur détecté (< 25 caractères réels) : déclenchement de l'OCR Tesseract haute fidélité
          if (pageText.length < 25) {
            setHasScannedPages(true);
            setSearchProgress({ current: pageNum, total: numPages, phase: 'ocr', ocrPercent: 0 });

            const ocrResult = await extractPageTextWithOcr(page, (info) => {
              setSearchProgress({
                current: pageNum,
                total: numPages,
                phase: 'ocr',
                ocrPercent: info.percent,
              });
            });

            if (ocrResult.text.length > 0) {
              pageText = ocrResult.text;
              isOcr = true;
              extractedTextCache.current.set(pageNum, { 
                text: pageText, 
                isOcr: true,
                words: ocrResult.words,
                canvasWidth: ocrResult.canvasWidth,
                canvasHeight: ocrResult.canvasHeight,
              });
            } else {
              extractedTextCache.current.set(pageNum, { text: pageText, isOcr: false });
            }
          } else {
            extractedTextCache.current.set(pageNum, { text: pageText, isOcr: false });
          }
          await pdfDoc.destroy().catch(() => {});
        }

        if (pageText) {
          const { matches, nextCounter } = findMatchesInPageText(
            pageText,
            query,
            pageNum,
            isOcr,
            matchCounter
          );
          matchCounter = nextCounter;

          if (matches.length > 0) {
            allMatches.push(...matches);
            setSearchResults([...allMatches]);

            // Naviguer immédiatement vers la première occurrence dès qu'elle est trouvée
            if (allMatches.length === matches.length) {
              setCurrentPage(matches[0].pageNumber);
              if (viewMode === 'all') {
                scrollToPage(matches[0].pageNumber);
              }
            }
          }
        }
      } catch (err) {
        console.warn(`Erreur traitement texte page ${pageNum}:`, err);
      }
    }

    setIsSearching(false);
    setSearchProgress(null);
    if (allMatches.length > 0) {
      setCurrentMatchIndex(0);
      const first = allMatches[0];
      setCurrentPage(first.pageNumber);
      if (viewMode === 'all') {
        scrollToPage(first.pageNumber);
      }
    }
  };

  const handleCancelSearch = () => {
    searchAbortRef.current = true;
    setIsSearching(false);
    setSearchProgress(null);
  };

  const handleClearSearch = () => {
    searchAbortRef.current = true;
    setIsSearching(false);
    setSearchProgress(null);
    setSearchQuery('');
    setActiveSearchTerm('');
    setSearchResults([]);
    setCurrentMatchIndex(0);
  };

  // Recherche instantanée dès la saisie (As you type)
  React.useEffect(() => {
    const trimmed = searchQuery.trim();
    if (!trimmed) {
      searchAbortRef.current = true;
      setActiveSearchTerm('');
      setSearchResults([]);
      setCurrentMatchIndex(0);
      setIsSearching(false);
      setSearchProgress(null);
      return;
    }

    if (trimmed.length < 2) {
      setActiveSearchTerm('');
      setSearchResults([]);
      return;
    }

    // Dès 2 lettres : activation immédiate du surlignage/soulignage visuel sur la page affichée
    setActiveSearchTerm(trimmed);

    // Indexation asynchrone avec debounce léger pour ne pas surcharger pendant la frappe rapide
    const timer = setTimeout(() => {
      handleStartSearch(trimmed, false);
    }, 220);

    return () => clearTimeout(timer);
  }, [searchQuery, document?.id, numPages]);

  const goToNextMatch = () => {
    if (searchResults.length === 0) return;
    const next = (currentMatchIndex + 1) % searchResults.length;
    setCurrentMatchIndex(next);
    const target = searchResults[next];
    setCurrentPage(target.pageNumber);
    if (viewMode === 'all') {
      scrollToPage(target.pageNumber);
    }
  };

  const goToPrevMatch = () => {
    if (searchResults.length === 0) return;
    const prev = (currentMatchIndex - 1 + searchResults.length) % searchResults.length;
    setCurrentMatchIndex(prev);
    const target = searchResults[prev];
    setCurrentPage(target.pageNumber);
    if (viewMode === 'all') {
      scrollToPage(target.pageNumber);
    }
  };

  // Défilement automatique du volet des miniatures sur la page active
  React.useEffect(() => {
    if (!isSidebarOpen || sidebarTab !== 'thumbnails') return;
    const thumbEl = thumbnailRefs.current.get(currentPage);
    if (thumbEl) {
      thumbEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [currentPage, isSidebarOpen, sidebarTab]);

  // Secure in-memory data state
  const [pdfData, setPdfData] = React.useState<File | null>(null);
  const [pdfError, setPdfError] = React.useState<boolean>(false);
  const [fileNotSupported, setFileNotSupported] = React.useState<boolean>(false);

  // Cache LRU en mémoire vive des octets de pages (Max 20 pages en RAM pour protéger les performances)
  const pageDataCache = React.useRef<LruPageCache>(new LruPageCache(20));
  const [activePageData, setActivePageData] = React.useState<Uint8Array | null>(null);
  const [isPageLoading, setIsPageLoading] = React.useState<boolean>(false);

  // Préchargement automatique du JSON OCR serveur dès l'ouverture de l'acte
  React.useEffect(() => {
    if (!document?.id) return;
    let isMounted = true;

    apiClient.get<any>(`/api/documents/${document.id}/ocr`)
      .then(ocrData => {
        if (!isMounted || !ocrData?.pages || !Array.isArray(ocrData.pages)) return;

        for (const p of ocrData.pages) {
          const pNum = p.pageNumber;
          const wordsList = extractWordsFromPage(p);

          extractedTextCache.current.set(pNum, {
            text: p.text || '',
            isOcr: true,
            words: wordsList,
            canvasWidth: p.width || 595,
            canvasHeight: p.height || 842,
          });
        }

        setHasScannedPages(true);
        setOcrVersion(v => v + 1);
      })
      .catch(() => {});

    return () => {
      isMounted = false;
    };
  }, [document?.id]);

  // Récupération automatique du nombre total de pages (< 1ms grâce au PageCount en BDD)
  React.useEffect(() => {
    if (!document?.id) return;
    setCurrentPage(1);
    setPdfError(false);
    apiClient.get<{ totalPages: number }>(`/api/documents/${document.id}/pages/info`)
      .then(res => {
        const total = (res as any)?.totalPages ?? (res as any)?.TotalPages;
        if (total && total > 0) {
          setNumPages(total);
        }
      })
      .catch(err => {
        console.warn("Info pages :", err);
      });
  }, [document?.id]);

  // Chargement de la page courante pour le mode page par page ('single')
  React.useEffect(() => {
    if (!document?.id || !authUser) {
      setActivePageData(null);
      return;
    }

    const targetPage = currentPage;
    const cached = pageDataCache.current.get(targetPage);

    // Si la page est déjà présente en cache LRU : affichage immédiat (0 ms)
    if (cached && cached.byteLength > 0) {
      setActivePageData(cached);
      setIsPageLoading(false);
      setPdfError(false);
      return;
    }

    // En mode continu 'all', c'est chaque VirtualPageItem qui se charge lui-même
    if (viewMode === 'all') {
      return;
    }

    const abortController = new AbortController();
    let isMounted = true;
    setIsPageLoading(true);

    apiClient.getArrayBuffer(
      `/api/documents/${document.id}/pages/${targetPage}`,
      { signal: abortController.signal }
    )
      .then(bytes => {
        if (!isMounted) return;
        pageDataCache.current.set(targetPage, bytes);
        setActivePageData(bytes);
        setIsPageLoading(false);
        setPdfError(false);
      })
      .catch(err => {
        if (!isMounted || abortController.signal.aborted || err?.name === 'AbortError') return;
        console.error(`Erreur chargement page ${targetPage}:`, err);
        setIsPageLoading(false);
        setPdfError(true);
      });

    // Préchauffage silencieux uniquement de la page suivante (+1)
    let prefetchTimer: any = null;
    const nextP = targetPage + 1;
    if (numPages && nextP <= numPages && !pageDataCache.current.has(nextP)) {
      prefetchTimer = setTimeout(() => {
        apiClient.getArrayBuffer(
          `/api/documents/${document.id}/pages/${nextP}`,
          { signal: abortController.signal }
        )
          .then(bytes => pageDataCache.current.set(nextP, bytes))
          .catch(() => {});
      }, 250);
    }

    return () => {
      isMounted = false;
      abortController.abort();
      if (prefetchTimer) clearTimeout(prefetchTimer);
    };
  }, [document?.id, authUser, currentPage, numPages, viewMode]);

  // Nettoyage complet lors du changement de document
  React.useEffect(() => {
    return () => {
      pageDataCache.current.clear();
      extractedTextCache.current.clear();
      setActivePageData(null);
      setSearchResults([]);
      setSearchQuery('');
      setActiveSearchTerm('');
      setCurrentMatchIndex(0);
    };
  }, [document?.id]);

  const singlePageFileProp = React.useMemo(() => {
    if (!activePageData || activePageData.byteLength === 0) return null;
    return { data: activePageData.slice() };
  }, [activePageData]);

  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    setPdfError(false);
  }

  // Block Ctrl+S / Ctrl+P / Ctrl+A / Ctrl+U (Protection anti-copie & anti-téléchargement)
  // Ne bloque JAMAIS dans les champs de formulaire pour permettre Ctrl+A, copier, coller librement
  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!authUser) return;
      const target = e.target as HTMLElement | null;
      const isInputOrEditable = Boolean(
        target && (
          target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable ||
          target.closest?.('input, textarea, [contenteditable="true"]')
        )
      );

      // Si l'utilisateur est dans un champ de saisie, laisser toutes les actions clavier s'exécuter
      if (isInputOrEditable) {
        return;
      }

      if ((e.ctrlKey || e.metaKey) && ['s', 'p', 'a', 'u'].includes(e.key.toLowerCase())) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener('keydown', handler, { capture: true });
    return () => window.removeEventListener('keydown', handler, { capture: true });
  }, [authUser]);

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (viewerRef.current?.requestFullscreen) {
        viewerRef.current.requestFullscreen().catch(() => {});
      }
      setIsFullscreen(true);
    } else {
      if (window.document.fullscreenElement && window.document.exitFullscreen) {
        window.document.exitFullscreen().catch(() => {});
      }
      setIsFullscreen(false);
    }
  };

  React.useEffect(() => {
    const handleFsChange = () => {
      setIsFullscreen(Boolean(window.document.fullscreenElement));
    };
    window.document.addEventListener('fullscreenchange', handleFsChange);
    return () => {
      window.document.removeEventListener('fullscreenchange', handleFsChange);
    };
  }, []);

  // Body scroll lock + Escape to exit fullscreen
  React.useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsFullscreen(false);
    };
    if (isFullscreen) {
      window.document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleEsc);
    } else {
      window.document.body.style.overflow = '';
    }
    return () => {
      window.document.body.style.overflow = '';
      window.removeEventListener('keydown', handleEsc);
    };
  }, [isFullscreen]);

  const handleSaveNote = async () => {
    if (!document || !authUser) return;
    const trimmed = noteContent.trim();
    if (!trimmed) {
      triggerToast("Veuillez rédiger une note.");
      return;
    }
    if (noteContent.length > 500) {
      triggerToast("La note ne peut pas dépasser 500 caractères.");
      return;
    }
    const alreadyExists = documentNotes.some(n => n.content.toLowerCase().trim() === trimmed.toLowerCase());
    if (alreadyExists) {
      triggerToast("Cette note existe déjà pour ce document.");
      return;
    }
    try {
      setNoteSaving(true);
      const savedNote = await noteService.saveNoteForDocument(document.id, noteContent);
      setDocumentNotes(prev => [savedNote, ...prev]);
      setNoteContent('');
      setNoteSavedFeedback(true);
      setTimeout(() => setNoteSavedFeedback(false), 2000);
    } catch (err: any) {
      triggerToast(err?.message || "Erreur lors de l'enregistrement de la note.");
    } finally {
      setNoteSaving(false);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    const success = await noteService.deleteNoteById(noteId);
    if (success) {
      setDocumentNotes(prev => prev.filter(n => n.id !== noteId));
    } else {
      triggerToast("Impossible de supprimer la note.");
    }
  };

  const [isDownloading, setIsDownloading] = React.useState(false);

  const handleSimulateDownload = async () => {
    if (!document) return;
    if (!authUser) {
      openAuthModal();
      return;
    }
    if (!canDownload) {
      triggerToast("⛔ Votre formule actuelle ne vous donne pas accès au téléchargement. Contactez notre équipe pour passer à une formule supérieure. ⛔");
      return;
    }
    
    try {
      setIsDownloading(true);
      const ticket = await documentService.requestDownloadTicket(document.id);
      addDownload(document); // track in UI if needed
      
      // Use the backend download stream route, passing the ticket in the query string
      // Téléchargement direct et sécurisé via Blob (évite les erreurs 404 de navigation SPA)
      const blob = await apiClient.getBlob(`/api/documents/${document.id}/download-stream?ticket=${ticket}`);
      const blobUrl = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = blobUrl;
      const safeName = (document.referenceNumber || document.officialTitle || 'document_juridique')
        .replace(/[\/\\:*?"<>|]/g, '_')
        .substring(0, 100);
      link.download = `${safeName}.pdf`;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      
      // Enregistre explicitement le téléchargement dans l'historique utilisateur
      downloadService.recordDownload(document.id).catch(() => {});

      triggerToast("Téléchargement du document réussi !");
    } catch (error: any) {
      if (error?.status === 429) {
        triggerToast("Vous avez atteint votre limite journalière de téléchargement.");
      } else if (error?.status === 401 || error?.status === 403) {
        triggerToast("Vous n'avez pas l'autorisation de télécharger ce document.");
      } else {
        triggerToast("Erreur lors de la génération du ticket de téléchargement.");
      }
    } finally {
      setIsDownloading(false);
    }
  };

  const handleFavButtonClick = () => {
    if (!authUser) {
      openAuthModal();
      return;
    }
    if (!document) return;
    setIsFolderModalOpen(true);
  };

  const handleCopyLink = () => {
    if (!document) return;
    const shareUrl = `${window.location.origin}/document/${document.id}`;
    navigator.clipboard.writeText(shareUrl)
      .then(() => triggerToast("🔗 Lien de partage copié dans le presse-papiers avec succès !"))
      .catch(() => triggerToast("Copie impossible. Utilisez l'adresse dans votre navigateur."));
  };

  // Find related texts in our database
  const relatedTextsDetails = React.useMemo(() => {
    if (!document) return [];
    const list: { id: number; officialTitle: string; relationType: string }[] = [];
    if (document.id === 1) { // Decret 2023
      list.push({ id: 7, officialTitle: "Décret N°95/031 du 24 Février 1995 fixant l'organisation des Huissiers", relationType: "Modifie et remplace" });
      list.push({ id: 8, officialTitle: "Décret N°85/009 du 12 Janvier 1985 fixant l'ancien règlement", relationType: "Abroge" });
    } else if (document.id === 7) { // Decret 1995
      list.push({ id: 1, officialTitle: "Décret N°2023/042 du 25 Janvier 2023 (Nouveau statut)", relationType: "Modifié par" });
    } else if (document.id === 8) { // Decret 1985
      list.push({ id: 1, officialTitle: "Décret N°2023/042 du 25 Janvier 2023 (Nouveau statut)", relationType: "Abrogé par" });
    }
    return list;
  }, [document?.id]);

  // Find similar texts in legal domain
  const similarTexts = allDocuments
    .filter(d => document && d.id !== document.id && d.areaOfLaw === document.areaOfLaw)
    .slice(0, 3);

  if (loading) {
    return <div className="p-20 text-center text-zinc-500 animate-pulse font-bold">Chargement de l'acte...</div>;
  }

  if (!document) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center space-y-6 px-4">
        <div className="bg-zinc-100 p-6 rounded-full shadow-inner">
          <FileQuestion className="w-16 h-16 text-zinc-400" />
        </div>
        <div className="max-w-md space-y-3">
          <h2 className="text-2xl font-bold text-zinc-800 tracking-tight">Acte Juridique Introuvable</h2>
          <p className="text-sm text-zinc-500 leading-relaxed">
            Désolé, le document que vous recherchez n'existe pas ou a été retiré de la base de données. 
            Veuillez vérifier l'identifiant de l'acte ou retourner à l'explorateur.
          </p>
        </div>
        <button 
          onClick={() => setView('categories')}
          className="mt-4 px-6 py-3 bg-violet-600 hover:bg-violet-700 text-white text-xs font-bold uppercase tracking-widest rounded-xl transition-all shadow-md shadow-violet-600/20"
        >
          Parcourir les actes
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6 relative">
      
      {/* Toast Alert Banner */}
      {toastMessage && (
        <div 
          style={{ backgroundColor: '#18181b', color: '#ffffff', borderColor: '#27272a' }}
          className="fixed bottom-6 right-6 z-55 bg-zinc-900 border border-zinc-800 text-white py-3.5 px-5 rounded-2xl shadow-2xl flex items-center gap-3 animate-fade-in max-w-sm"
        >
          <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
          <p className="text-xs font-semibold leading-relaxed" style={{ color: '#ffffff' }}>{toastMessage}</p>
        </div>
      )}

      {/* CORE NAVIGATION & ACTION BAR (Design exclusif Docarn) */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-gradient-to-r from-white via-primary-50/30 to-white p-3 sm:px-4.5 sm:py-2.5 border border-primary-100/80 rounded-2xl shadow-xs">
        <div className="flex items-center gap-2 text-xs min-w-0">
          <button
            onClick={() => setView('search')}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-primary-750 hover:text-primary-900 bg-primary-50 hover:bg-primary-100/80 border border-primary-200/70 font-bold text-xs transition cursor-pointer shrink-0 shadow-2xs"
            title="Revenir aux résultats de recherche"
          >
            <ChevronLeft className="w-4 h-4 text-primary-750" />
            <span>Recherche</span>
          </button>

          <span className="text-zinc-300 shrink-0">/</span>

          <nav className="flex items-center gap-1.5 text-zinc-500 font-medium truncate text-xs">
            <button 
              onClick={() => setView('home')} 
              className="hover:text-primary-750 transition cursor-pointer shrink-0"
            >
              Accueil
            </button>
            <span className="text-zinc-300 shrink-0">/</span>
            {authUser && document.nature ? (
              <>
                <span className="capitalize text-zinc-600 shrink-0">{formatNature(document.nature)}s</span>
                <span className="text-zinc-300 shrink-0">/</span>
              </>
            ) : null}
            <span className="text-zinc-900 font-extrabold truncate">
              {authUser ? (document.referenceNumber || document.officialTitle) : "Fiche de l'acte"}
            </span>
          </nav>
        </div>

        {/* Action pills on the right */}
        <div className="flex items-center gap-2 shrink-0">
          {/* Copy Share Link */}
          <button
            onClick={handleCopyLink}
            className="inline-flex items-center gap-1.5 bg-white hover:bg-primary-50/70 border border-zinc-200 hover:border-primary-200 text-zinc-700 hover:text-primary-750 px-3.5 py-1.5 text-xs font-bold rounded-full transition-all cursor-pointer shadow-2xs"
            title="Copier le lien direct de l'acte"
          >
            <Link className="w-3.5 h-3.5 text-zinc-500" />
            <span className="hidden sm:inline">Partager</span>
          </button>

          {/* Favorites Star */}
          <button
            type="button"
            onClick={handleFavButtonClick}
            className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-bold rounded-full transition-colors border cursor-pointer ${
              isFav 
                ? 'bg-amber-50 border-amber-300 text-amber-800 shadow-2xs' 
                : 'bg-white hover:bg-zinc-50 border-zinc-200 text-zinc-700 shadow-2xs'
            }`}
            title={isFav ? "Gérer le classement de cet acte dans vos dossiers" : "Classer cet acte dans vos dossiers favoris"}
          >
            <Star className={`w-3.5 h-3.5 ${isFav ? 'fill-current text-amber-500' : 'text-zinc-400'}`} />
            <span>{isFav ? "Classé" : "Favori"}</span>
          </button>

          {/* Download */}
          {!authUser ? (
            <button
              onClick={() => openAuthModal()}
              className="inline-flex items-center gap-1.5 bg-amber-600 hover:bg-amber-700 text-white px-4 py-1.5 text-xs font-bold rounded-full transition-colors cursor-pointer shadow-xs border-none"
            >
              <Lock className="w-3.5 h-3.5" />
              <span>Télécharger</span>
            </button>
          ) : !canDownload ? (
            <button
              type="button"
              onClick={() => triggerToast("⛔ Votre formule actuelle ne vous donne pas accès au téléchargement. Contactez notre équipe pour passer à une formule supérieure. ⛔")}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-bold rounded-full bg-zinc-100 text-zinc-400 border border-zinc-200 cursor-not-allowed select-none"
              title="Téléchargement non inclus dans votre formule"
            >
              <Lock className="w-3.5 h-3.5" />
              <span>Télécharger (PDF)</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSimulateDownload}
              disabled={isDownloading}
              className={`inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-bold rounded-full transition-all cursor-pointer shadow-sm border-none ${
                isDownloading 
                  ? 'bg-zinc-400 text-white cursor-not-allowed' 
                  : 'bg-gradient-to-r from-primary-750 to-primary-600 hover:from-primary-800 hover:to-primary-700 text-white shadow-primary-750/25'
              }`}
            >
              {isDownloading ? (
                <>
                  <span className="w-3.5 h-3.5 rounded-full border-2 border-white/30 border-t-white animate-spin"></span>
                  <span>Préparation...</span>
                </>
              ) : (
                <>
                  <DownloadCloud className="w-3.5 h-3.5" />
                  <span>Télécharger (PDF)</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* MAIN DOCUMENT HEADINGS (Elevated card) */}
      <div className="bg-white p-5 sm:p-7 rounded-2xl border border-zinc-200/80 shadow-xs space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          {authUser ? (
            <>
              {document.referenceNumber && (
                <span className="font-mono text-xs font-bold text-zinc-900 bg-zinc-100 border border-zinc-200 px-2.5 py-1 rounded-lg">
                  {document.referenceNumber}
                </span>
              )}
              {document.nature && (
                <span className="text-[11px] font-bold bg-primary-50 text-primary-750 border border-primary-200/50 px-2.5 py-1 rounded-full uppercase tracking-wide">
                  {formatNature(document.nature)}
                </span>
              )}
              <LegalOriginTag origin={document.legalOrigin} size="sm" />
              <StatusBadge status={document.legalStatus} size="sm" />
              {document.effectiveYear && (
                <span className="text-xs text-zinc-600 font-semibold bg-zinc-50 border border-zinc-200 px-2.5 py-1 rounded-md flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5 text-zinc-400" /> Année {document.effectiveYear}
                </span>
              )}
            </>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200 px-3 py-1 rounded-md">
                <Lock className="w-3 h-3 text-slate-500" /> Analyse & Métadonnées abonnés
              </span>
              {document.hasPdf && (
                <span className="text-[11px] font-semibold bg-slate-50 text-slate-700 border border-slate-200 px-2.5 py-0.5 rounded-md flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3 text-emerald-600" /> PDF intégral
                </span>
              )}
              {document.hasDate && (
                <span className="text-[11px] font-semibold bg-slate-50 text-slate-700 border border-slate-200 px-2.5 py-0.5 rounded-md flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3 text-blue-600" /> Date & Vigueur
                </span>
              )}
              {document.hasJurisdiction && (
                <span className="text-[11px] font-semibold bg-slate-50 text-slate-700 border border-slate-200 px-2.5 py-0.5 rounded-md flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3 text-indigo-600" /> Autorité & Domaine
                </span>
              )}
            </div>
          )}
        </div>

        <h1 className="font-display text-xl sm:text-2xl font-extrabold text-zinc-900 leading-snug tracking-tight break-words">
          {document.officialTitle}
        </h1>

        {authUser && document.issuingAuthority && (
          <div className="flex items-center gap-2 text-xs text-zinc-500 font-medium pt-1">
            <Landmark className="w-3.5 h-3.5 text-zinc-400" />
            <span>Autorité émettrice : <strong className="text-zinc-800 font-semibold">{document.issuingAuthority}</strong></span>
          </div>
        )}
      </div>

      {/* METADATA SHEETS SIDEBAR & GORGEOUS MOCK PDF INTEGRATED VIEW */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        
        {/* Left Column: METADATA & RELATIONS */}
        <div className="lg:col-span-4 bg-white p-6 rounded-2xl border border-zinc-200/80 shadow-xs space-y-6">
          <h4 className="text-xs uppercase font-extrabold text-zinc-900 border-b border-zinc-150 pb-3 tracking-wider flex items-center justify-between">
            <span className="flex items-center gap-2">
              <FileText className="w-4.5 h-4.5 text-slate-700" /> Fiche de métadonnées
            </span>
            {!authUser && <Lock className="w-3.5 h-3.5 text-slate-500" />}
          </h4>

          {!authUser ? (
            /* Mode non authentifié (Protégé & Incitatif) */
            <div className="space-y-5">
              <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-4 space-y-3 shadow-2xs">
                <div className="flex items-center gap-1.5 text-slate-900 font-bold text-xs">
                  <Lock className="w-3.5 h-3.5 text-slate-600 shrink-0" />
                  <span>Fiche détaillée protégée</span>
                </div>
                <p className="text-[11px] text-slate-600 leading-relaxed">
                  La référence officielle, les dates d'application, le domaine du droit et les descripteurs juridiques sont réservés aux comptes abonnés.
                </p>
                <button
                  type="button"
                  onClick={openAuthModal}
                  className="w-full py-2.5 px-3 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs rounded-xl transition shadow-sm cursor-pointer flex items-center justify-center gap-2"
                >
                  <Lock className="w-3.5 h-3.5" /> Se connecter / S'abonner
                </button>
              </div>

              <div className="space-y-3 text-xs opacity-70 select-none">
                <div className="flex items-center justify-between py-2 border-b border-zinc-100">
                  <span className="text-zinc-400 font-bold uppercase text-[9px] tracking-wider">Référence</span>
                  <span className="font-mono text-zinc-500 font-semibold flex items-center gap-1">
                    <Lock className="w-3 h-3 text-zinc-400" /> Réservée
                  </span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-zinc-100">
                  <span className="text-zinc-400 font-bold uppercase text-[9px] tracking-wider">Auteur Émetteur</span>
                  <span className="text-zinc-500 font-medium flex items-center gap-1">
                    <Lock className="w-3 h-3 text-zinc-400" /> Réservé
                  </span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-zinc-100">
                  <span className="text-zinc-400 font-bold uppercase text-[9px] tracking-wider">Adoption & Vigueur</span>
                  <span className="text-zinc-500 font-medium flex items-center gap-1">
                    <Lock className="w-3 h-3 text-zinc-400" /> Réservées
                  </span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-zinc-100">
                  <span className="text-zinc-400 font-bold uppercase text-[9px] tracking-wider">Domaine du Droit</span>
                  <span className="text-zinc-500 font-medium flex items-center gap-1">
                    <Lock className="w-3 h-3 text-zinc-400" /> Réservé
                  </span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-zinc-100">
                  <span className="text-zinc-400 font-bold uppercase text-[9px] tracking-wider">Catégorie</span>
                  <span className="text-zinc-500 font-medium flex items-center gap-1">
                    <Lock className="w-3 h-3 text-zinc-400" /> Masquée
                  </span>
                </div>
              </div>

              <hr className="border-zinc-100" />

              <div className="space-y-2">
                <span className="block text-[10px] font-bold uppercase text-zinc-400 tracking-wider">Liens Connexes & Antécédents</span>
                <div className="p-3.5 bg-zinc-100/80 border border-dashed border-zinc-200 rounded-xl text-center space-y-1">
                  <Lock className="w-3.5 h-3.5 text-zinc-400 mx-auto" />
                  <p className="text-[10px] text-zinc-500 font-medium">L'historique des modifications et abrogations est réservé aux abonnés.</p>
                </div>
              </div>
            </div>
          ) : (
            /* Mode authentifié (Affichage complet des métadonnées) */
            <>
              <div className="space-y-4 text-xs">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-y-1 sm:gap-y-0 py-2 border-b border-zinc-100">
                  <span className="text-zinc-400 font-bold uppercase text-[9px] tracking-wider">Référence</span>
                  <span className="sm:col-span-2 font-mono font-bold text-zinc-900 break-words">{document.referenceNumber}</span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-y-1 sm:gap-y-0 py-2 border-b border-zinc-100">
                  <span className="text-zinc-400 font-bold uppercase text-[9px] tracking-wider">Auteur Émetteur</span>
                  <span className="sm:col-span-2 font-semibold text-zinc-800 break-words">{document.issuingAuthority || "Non classé"}</span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-y-1 sm:gap-y-0 py-2 border-b border-zinc-100">
                  <span className="text-zinc-400 font-bold uppercase text-[9px] tracking-wider">Adoption</span>
                  <span className="sm:col-span-2 text-zinc-700 font-semibold">{formatFrenchDate(document.dateOfAdoption)}</span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-y-1 sm:gap-y-0 py-2 border-b border-zinc-100">
                  <span className="text-zinc-400 font-bold uppercase text-[9px] tracking-wider">En vigueur</span>
                  <span className="sm:col-span-2 text-zinc-700 font-semibold">{formatFrenchDate(document.effectiveDate)}</span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-y-1 sm:gap-y-0 py-2 border-b border-zinc-100">
                  <span className="text-zinc-400 font-bold uppercase text-[9px] tracking-wider">Domaine</span>
                  <span className="sm:col-span-2 text-primary-750 font-bold">{formatDomain(document.areaOfLaw)}</span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-y-1 sm:gap-y-0 py-2 border-b border-zinc-100">
                  <span className="text-zinc-400 font-bold uppercase text-[9px] tracking-wider">Catégorie</span>
                  <span className="sm:col-span-2 text-zinc-700 font-semibold">{document.categoryName}</span>
                </div>
              </div>

              {/* Keywords metadata block */}
              <div className="space-y-2">
                <span className="block text-[9px] font-bold uppercase text-zinc-400 tracking-wider">Descripteurs thématiques</span>
                <div className="flex flex-wrap gap-1.5">
                  {(document.legalKeywords || []).map((kwIdx) => (
                    <span key={kwIdx} className="bg-white border border-zinc-200 text-zinc-700 font-bold text-[10px] px-2.5 py-0.5 rounded-lg shadow-sm">
                      {kwIdx}
                    </span>
                  ))}
                </div>
              </div>

              <hr className="border-zinc-100" />

              {/* LEGALLY CONNECTED RELATION FILES */}
              <div className="space-y-3">
                <span className="block text-[10px] font-bold uppercase text-zinc-400 tracking-wider">Liens Connexes & Antécédents</span>
                
                {relatedTextsDetails.length === 0 ? (
                  <p className="text-[11px] text-zinc-400 font-light italic">Aucune relation historique référencée.</p>
                ) : (
                  <div className="space-y-2">
                    {relatedTextsDetails.map((rel) => (
                      <button
                        key={rel.id}
                        onClick={() => setActiveDocumentId(rel.id)}
                        className="w-full text-left p-4 bg-white hover:bg-primary-50/20 border border-zinc-200/80 hover:border-primary-200/40 rounded-xl block transition-all cursor-pointer group"
                      >
                        <span className="block font-bold text-[9px] text-primary-750 uppercase tracking-widest mb-1">
                          {rel.relationType}
                        </span>
                        <span className="block text-xs font-semibold text-zinc-800 leading-normal line-clamp-2 group-hover:text-primary-750 transition-colors">
                          {rel.officialTitle}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Best Match Alert */}
          {bestMatch && bestMatch.highlight && (
            <div className="bg-slate-50 border border-slate-200 p-5 rounded-2xl shadow-sm relative overflow-hidden">
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-slate-800"></div>
              <h4 className="flex items-center gap-2 font-bold text-sm text-slate-900 mb-2">
                <Lightbulb className="w-4 h-4 text-slate-700" />
                Extrait pertinent identifié
              </h4>
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs text-slate-600 font-medium">
                  Passage correspondant à votre requête :
                </p>
                {bestMatch.pages && bestMatch.pages.length > 0 && (
                  <span className="flex items-center gap-1.5 bg-white text-slate-700 text-[10px] font-bold px-2.5 py-1 rounded-md shadow-2xs border border-slate-200 uppercase tracking-wider shrink-0 ml-2">
                    <FileText className="w-3 h-3 text-slate-500" />
                    Page {bestMatch.pages.length > 3 
                      ? `${bestMatch.pages[0]}, ${bestMatch.pages[1]} ... ${bestMatch.pages[bestMatch.pages.length - 1]}` 
                      : bestMatch.pages.join(', ')}
                  </span>
                )}
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-200/80 text-sm leading-relaxed text-zinc-700 italic shadow-2xs relative">
                {bestMatch.articleNumber && (
                  <strong className="not-italic text-slate-900 font-bold block mb-2 text-xs uppercase tracking-wider border-b border-slate-100 pb-1">
                    {bestMatch.articleNumber}
                  </strong>
                )}
                <span
                  className="[&_strong]:font-bold [&_strong]:not-italic [&_strong]:bg-slate-100 [&_strong]:text-slate-900 [&_strong]:px-1 [&_strong]:rounded"
                  dangerouslySetInnerHTML={{ __html: `"...${bestMatch.highlight}..."` }}
                />
              </div>
            </div>
          )}

        </div>

        {/* Right Column: PDF VIEWER & PRIVATE NOTES */}
        <div className="lg:col-span-8 space-y-6">
          
          {/* PDF Viewer — wrapper supporte le plein écran */}
          <div ref={viewerRef} className={isFullscreen ? 'fixed inset-0 z-[9999] flex flex-col bg-zinc-950 w-screen h-screen' : 'print:hidden'}>

          {/* PDF Viewer toolbar (Design Épuré & Lumineux Docarn) */}
          <div className={`bg-white/95 backdrop-blur-md text-zinc-800 px-3.5 sm:px-4 py-2 flex items-center justify-between shadow-xs text-xs border border-zinc-200/90 ${isFullscreen ? '' : 'rounded-t-2xl'}`}>
            <div className="flex items-center gap-2 min-w-0">
              {/* Docarn Official Logo Icon */}
              <div className="flex items-center gap-2 pr-2.5 border-r border-zinc-200 shrink-0">
                <img src="/icon_docarn.png" alt="Docarn" className="w-5 h-5 object-contain" />
                <span className="hidden sm:inline font-bold text-xs tracking-tight text-zinc-900">
                  Docarn
                </span>
              </div>

              {/* Toggle Volet Pages & Recherche (Disponibles uniquement pour utilisateurs connectés) */}
              {authUser ? (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      if (isSidebarOpen && sidebarTab === 'thumbnails') {
                        setIsSidebarOpen(false);
                      } else {
                        setIsSidebarOpen(true);
                        setSidebarTab('thumbnails');
                      }
                    }}
                    className={`flex items-center gap-1.5 px-2.5 py-1.2 rounded-lg text-xs font-bold transition-all cursor-pointer border ${
                      isSidebarOpen && sidebarTab === 'thumbnails'
                        ? 'bg-primary-50 text-primary-750 border-primary-200/90 shadow-2xs'
                        : 'bg-zinc-50 hover:bg-zinc-100 text-zinc-600 hover:text-zinc-900 border-zinc-200/80'
                    }`}
                    title="Afficher/masquer les miniatures de pages"
                  >
                    <Layers className={`w-3.5 h-3.5 ${isSidebarOpen && sidebarTab === 'thumbnails' ? 'text-primary-750' : 'text-zinc-500'}`} />
                    <span className="hidden sm:inline text-[11px]">Pages</span>
                    {numPages && (
                      <span className="text-[10px] font-mono opacity-80">({numPages})</span>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      if (isSidebarOpen && sidebarTab === 'search') {
                        setIsSidebarOpen(false);
                      } else {
                        setIsSidebarOpen(true);
                        setSidebarTab('search');
                        setTimeout(() => searchInputRef.current?.focus(), 80);
                      }
                    }}
                    className={`flex items-center gap-1.5 px-2.5 py-1.2 rounded-lg text-xs font-bold transition-all cursor-pointer border ${
                      isSidebarOpen && sidebarTab === 'search'
                        ? 'bg-primary-50 text-primary-750 border-primary-200/90 shadow-2xs'
                        : 'bg-zinc-50 hover:bg-zinc-100 text-zinc-600 hover:text-zinc-900 border-zinc-200/80'
                    }`}
                    title="Rechercher dans le document (Ctrl+F)"
                  >
                    <Search className={`w-3.5 h-3.5 ${isSidebarOpen && sidebarTab === 'search' ? 'text-primary-750' : 'text-zinc-500'}`} />
                    <span className="hidden sm:inline text-[11px]">Recherche</span>
                    {searchResults.length > 0 && (
                      <span className="px-1.5 py-0.2 bg-amber-500 text-zinc-950 font-black rounded-full text-[9px]">
                        {searchResults.length}
                      </span>
                    )}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => openAuthModal()}
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold bg-primary-50 hover:bg-primary-100 text-primary-750 border border-primary-200/80 transition cursor-pointer shadow-2xs"
                  title="Connectez-vous pour accéder à la navigation par page et à la recherche"
                >
                  <Lock className="w-3.5 h-3.5 text-primary-600" />
                  <span className="hidden sm:inline text-[11px]">Connexion requise</span>
                </button>
              )}

              {/* Titre / Référence discret */}
              <div className="hidden md:flex items-center gap-1.5 px-2 py-1 text-zinc-500 max-w-[160px] lg:max-w-[220px] truncate text-[11px] font-mono">
                <FileText className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
                <span className="truncate">{document.referenceNumber || document.officialTitle}</span>
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              {/* Pagination compacte & discrète (Seulement pour utilisateur connecté) */}
              {(authUser && (activePageData || numPages)) && (
                <div className="flex items-center gap-0.5 bg-zinc-50 rounded-lg px-1.5 py-0.5 border border-zinc-200 shadow-2xs">
                  <button
                    onClick={() => {
                      const p = Math.max(1, currentPage - 1);
                      setCurrentPage(p);
                      scrollToPage(p);
                    }}
                    disabled={currentPage <= 1}
                    className="p-1 rounded hover:bg-zinc-200/70 transition-colors text-zinc-500 hover:text-zinc-900 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer border-none bg-transparent"
                    title="Page précédente"
                  >
                    <ChevronLeft className="w-3.5 h-3.5" />
                  </button>

                  <div className="flex items-center gap-1 px-0.5">
                    <input
                      type="number"
                      min={1}
                      max={numPages || 1}
                      value={pageInput}
                      onChange={(e) => setPageInput(e.target.value)}
                      onFocus={(e) => e.target.select()}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handlePageInputSubmit();
                        }
                      }}
                      onBlur={handlePageInputSubmit}
                      className="w-8 text-center bg-white text-zinc-900 text-xs font-mono font-bold rounded px-0.5 py-0.5 border border-zinc-200 focus:border-primary-600 focus:ring-1 focus:ring-primary-600 focus:outline-none transition-colors"
                      title="Page"
                    />
                    <span className="text-[11px] font-mono font-semibold text-zinc-400 select-none">
                      / {numPages || '...'}
                    </span>
                  </div>

                  <button
                    onClick={() => {
                      const p = numPages ? Math.min(numPages, currentPage + 1) : currentPage + 1;
                      setCurrentPage(p);
                      scrollToPage(p);
                    }}
                    disabled={numPages ? currentPage >= numPages : false}
                    className="p-1 rounded hover:bg-zinc-200/70 transition-colors text-zinc-500 hover:text-zinc-900 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer border-none bg-transparent"
                    title="Page suivante"
                  >
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}

              {/* Zoom controls compacts & légers */}
              <div className="flex items-center gap-0.5 bg-zinc-50 rounded-lg px-1 py-0.5 border border-zinc-200 shadow-2xs">
                <button
                  onClick={() => setPdfZoom(z => Math.max(50, z - 10))}
                  disabled={pdfZoom <= 50}
                  className="p-1 rounded hover:bg-zinc-200/70 transition-colors text-zinc-500 hover:text-zinc-900 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer border-none bg-transparent"
                  title="Zoom arrière"
                >
                  <ZoomOut className="w-3.5 h-3.5" />
                </button>
                <span className="text-[10px] font-mono font-bold text-zinc-600 w-8 text-center select-none">
                  {pdfZoom}%
                </span>
                <button
                  onClick={() => setPdfZoom(z => Math.min(200, z + 10))}
                  disabled={pdfZoom >= 200}
                  className="p-1 rounded hover:bg-zinc-200/70 transition-colors text-zinc-500 hover:text-zinc-900 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer border-none bg-transparent"
                  title="Zoom avant"
                >
                  <ZoomIn className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* Bouton plein écran léger */}
              <button
                onClick={toggleFullscreen}
                className={`flex items-center gap-1.5 px-2.5 py-1.2 rounded-lg text-xs font-bold transition-all cursor-pointer border ${
                  isFullscreen
                    ? 'bg-zinc-900 text-white border-zinc-900 shadow-xs'
                    : 'bg-zinc-50 hover:bg-zinc-100 text-zinc-700 hover:text-zinc-950 border-zinc-200 shadow-2xs'
                }`}
                title={isFullscreen ? 'Quitter le plein écran (Échap)' : 'Agrandir en plein écran'}
              >
                {isFullscreen ? (
                  <><Minimize2 className="w-3.5 h-3.5" /><span className="hidden sm:inline text-[11px]">Réduire</span></>
                ) : (
                  <><Maximize2 className="w-3.5 h-3.5" /><span className="hidden sm:inline text-[11px]">Plein écran</span></>
                )}
              </button>
            </div>
          </div>

          {/* PDF Viewport Content Container */}
          <div 
            className={`border-l border-r border-b border-zinc-200 bg-zinc-100 shadow-sm relative overflow-hidden flex transition-colors ${
              isFullscreen ? 'flex-1 border-0' : 'rounded-b-2xl h-[600px] sm:h-[800px]'
            }`}
            onContextMenu={(e) => e.preventDefault()}
          >
            {/* Volet latéral multifonction Docarn (Pages & Recherche dans le texte) - Uniquement si connecté */}
            {Boolean(authUser && isSidebarOpen) && (
              <aside className={`${
                sidebarTab === 'search' ? 'w-72 sm:w-80' : 'w-48 sm:w-56'
              } shrink-0 border-r border-zinc-200 bg-white/95 backdrop-blur-md flex flex-col z-20 transition-all duration-200 shadow-sm`}>
                
                {/* En-tête avec onglets Pages / Recherche */}
                <div className="p-2.5 border-b border-zinc-200/80 bg-zinc-50/70 flex items-center justify-between gap-1">
                  <div className="flex items-center bg-zinc-200/70 p-0.5 rounded-lg border border-zinc-300/40 w-full max-w-[230px]">
                    <button
                      type="button"
                      onClick={() => setSidebarTab('thumbnails')}
                      className={`flex-1 flex items-center justify-center gap-1 py-1 px-2 rounded-md text-[11px] font-bold transition-all cursor-pointer border-none ${
                        sidebarTab === 'thumbnails'
                          ? 'bg-white text-primary-750 shadow-xs'
                          : 'text-zinc-600 hover:text-zinc-900 bg-transparent'
                      }`}
                    >
                      <Layers className="w-3.5 h-3.5" />
                      <span>Pages</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setSidebarTab('search');
                        setTimeout(() => searchInputRef.current?.focus(), 50);
                      }}
                      className={`flex-1 flex items-center justify-center gap-1 py-1 px-2 rounded-md text-[11px] font-bold transition-all cursor-pointer border-none ${
                        sidebarTab === 'search'
                          ? 'bg-white text-primary-750 shadow-xs'
                          : 'text-zinc-600 hover:text-zinc-900 bg-transparent'
                      }`}
                    >
                      <Search className="w-3.5 h-3.5" />
                      <span>Recherche</span>
                      {searchResults.length > 0 && (
                        <span className="ml-0.5 px-1.5 py-0.2 bg-primary-750 text-white rounded-full text-[9px] font-extrabold">
                          {searchResults.length}
                        </span>
                      )}
                    </button>
                  </div>

                  <button
                    type="button"
                    onClick={() => setIsSidebarOpen(false)}
                    className="p-1 rounded-md hover:bg-zinc-200/60 transition-colors border-none bg-transparent cursor-pointer text-zinc-400 hover:text-zinc-700 shrink-0"
                    title="Masquer le volet"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>

                {/* CONTENU ONGLET 1 : MINIATURES RÉELLES DES PAGES */}
                {sidebarTab === 'thumbnails' && (
                  <div className="flex-1 overflow-y-auto p-2.5 space-y-2.5 custom-scrollbar">
                    {numPages && numPages > 0 ? (
                      Array.from({ length: numPages }, (_, index) => {
                        const p = index + 1;
                        const isActive = p === currentPage;
                        const pageMatchesCount = searchResults.filter(r => r.pageNumber === p).length;
                        return (
                          <div
                            key={`thumb_wrap_${p}`}
                            ref={(el) => {
                              if (el) thumbnailRefs.current.set(p, el);
                              else thumbnailRefs.current.delete(p);
                            }}
                            className="relative"
                          >
                            <ThumbnailPageItem
                              pageNumber={p}
                              documentId={String(document.id)}
                              isActive={isActive}
                              pageDataCache={pageDataCache}
                              onClick={() => {
                                setCurrentPage(p);
                                if (viewMode === 'all') scrollToPage(p);
                              }}
                            />
                            {pageMatchesCount > 0 && (
                              <span 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSidebarTab('search');
                                }}
                                className="absolute top-3 left-3 bg-amber-500 hover:bg-amber-600 text-zinc-950 font-black text-[9px] px-1.5 py-0.5 rounded-full shadow-md cursor-pointer ring-2 ring-white flex items-center gap-0.5"
                                title={`${pageMatchesCount} occurrence(s) trouvée(s) sur cette page`}
                              >
                                <Search className="w-2.5 h-2.5" />
                                {pageMatchesCount}
                              </span>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      <div className="p-4 text-center text-xs text-zinc-400">Chargement des pages...</div>
                    )}
                  </div>
                )}

                {/* CONTENU ONGLET 2 : RECHERCHE TEXTUELLE INTELLIGENTE */}
                {sidebarTab === 'search' && (
                  <div className="flex-1 flex flex-col overflow-hidden">
                    {/* Barre de recherche */}
                    <div className="p-3 border-b border-zinc-200/80 bg-zinc-50/50 space-y-2">
                      <div className="relative flex items-center">
                        <Search className="w-3.5 h-3.5 text-primary-750 absolute left-2.5 pointer-events-none" />
                        <input
                          ref={searchInputRef}
                          type="text"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleStartSearch(undefined, true);
                            }
                          }}
                          placeholder="Recherche instantanée (titre, article, terme...)"
                          className="w-full select-text bg-white text-zinc-900 pl-8 pr-7 py-1.5 text-xs rounded-xl border border-zinc-200 focus:border-primary-600 focus:ring-1 focus:ring-primary-600 focus:outline-none transition-all placeholder:text-zinc-400 font-medium"
                        />
                        {searchQuery && (
                          <button
                            type="button"
                            onClick={handleClearSearch}
                            className="absolute right-2 text-zinc-400 hover:text-zinc-600 border-none bg-transparent cursor-pointer p-0.5"
                            title="Effacer la recherche"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>

                      <div className="flex items-center justify-between gap-2">
                        <button
                          type="button"
                          onClick={() => handleStartSearch(undefined, true)}
                          disabled={isSearching || !searchQuery.trim()}
                          className="flex-1 py-1.5 px-3 bg-gradient-to-r from-primary-750 to-primary-600 hover:from-primary-800 hover:to-primary-700 text-white rounded-lg text-xs font-bold transition shadow-xs cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
                        >
                          {isSearching ? (
                            <>
                              <Sparkles className="w-3.5 h-3.5 animate-spin text-white" />
                              <span>Recherche en cours...</span>
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-3.5 h-3.5" />
                              <span>Recherche instantanée</span>
                            </>
                          )}
                        </button>
                        {isSearching && (
                          <button
                            type="button"
                            onClick={handleCancelSearch}
                            className="px-2.5 py-1.5 bg-zinc-200 hover:bg-zinc-300 text-zinc-700 rounded-lg text-xs font-bold transition cursor-pointer"
                          >
                            Arrêter
                          </button>
                        )}
                      </div>

                      {/* Barre de progression pendant l'indexation (Texte numérique & Docarn AI) */}
                      {searchProgress && (
                        searchProgress.phase === 'ocr' ? (
                          <div className="space-y-1.5 pt-1 animate-fade-in bg-primary-50/80 p-2.5 rounded-xl border border-primary-200/90 shadow-2xs">
                            <div className="flex items-center justify-between text-[10px] text-primary-950 font-bold">
                              <span className="flex items-center gap-1.5">
                                <Sparkles className="w-3.5 h-3.5 text-primary-600 animate-pulse" />
                                <span>Docarn AI • Page {searchProgress.current} / {searchProgress.total}</span>
                              </span>
                              <span className="font-mono text-primary-750 font-extrabold">{searchProgress.ocrPercent || 0}%</span>
                            </div>
                            <div className="w-full bg-primary-100 h-1.5 rounded-full overflow-hidden">
                              <div 
                                className="bg-primary-750 h-full transition-all duration-150"
                                style={{ width: `${searchProgress.ocrPercent || ((searchProgress.current / searchProgress.total) * 100)}%` }}
                              />
                            </div>
                            <p className="text-[9px] text-primary-850 font-medium leading-tight">
                              Docarn AI déchiffre automatiquement le contenu du document scanné...
                            </p>
                          </div>
                        ) : (
                          <div className="space-y-1 pt-1 animate-fade-in">
                            <div className="flex justify-between text-[10px] text-zinc-500 font-mono">
                              <span className="flex items-center gap-1">
                                <Sparkles className="w-3 h-3 text-primary-600" />
                                <span>Docarn AI • Scan direct...</span>
                              </span>
                              <span>Page {searchProgress.current} / {searchProgress.total}</span>
                            </div>
                            <div className="w-full bg-zinc-200 h-1.5 rounded-full overflow-hidden">
                              <div 
                                className="bg-primary-750 h-full transition-all duration-150"
                                style={{ width: `${(searchProgress.current / searchProgress.total) * 100}%` }}
                              />
                            </div>
                          </div>
                        )
                      )}
                    </div>

                    {/* Barre de navigation entre les correspondances */}
                    {searchResults.length > 0 && (
                      <div className="px-3 py-2 bg-primary-50/70 border-b border-primary-100 flex items-center justify-between text-xs">
                        <span className="text-[11px] font-bold text-primary-950">
                          <strong>{currentMatchIndex + 1}</strong> sur <strong>{searchResults.length}</strong> occurrence{searchResults.length > 1 ? 's' : ''}
                        </span>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={goToPrevMatch}
                            className="p-1 rounded-md bg-white border border-primary-200 hover:bg-primary-100 text-primary-750 transition cursor-pointer"
                            title="Occurrence précédente"
                          >
                            <ChevronUp className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={goToNextMatch}
                            className="p-1 rounded-md bg-white border border-primary-200 hover:bg-primary-100 text-primary-750 transition cursor-pointer"
                            title="Occurrence suivante"
                          >
                            <ChevronDown className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Liste des extraits de recherche */}
                    <div className="flex-1 overflow-y-auto p-2.5 space-y-2 custom-scrollbar">
                      {searchResults.length > 0 ? (
                        searchResults.map((res, idx) => {
                          const isCurrent = idx === currentMatchIndex;
                          return (
                            <div
                              key={res.id}
                              onClick={() => {
                                setCurrentMatchIndex(idx);
                                setCurrentPage(res.pageNumber);
                                if (viewMode === 'all') {
                                  scrollToPage(res.pageNumber);
                                }
                              }}
                              className={`p-2.5 rounded-xl border transition-all cursor-pointer group text-left ${
                                isCurrent
                                  ? 'border-primary-750 ring-2 ring-primary-500/25 bg-primary-50/90 shadow-sm'
                                  : 'border-zinc-200 bg-white hover:border-zinc-300 hover:bg-zinc-50 shadow-2xs'
                              }`}
                            >
                              <div className="flex items-center justify-between mb-1.5">
                                <div className="flex items-center gap-1.5">
                                  <span className={`text-[10px] font-mono font-extrabold px-2 py-0.5 rounded-md ${
                                    isCurrent ? 'bg-primary-750 text-white' : 'bg-zinc-100 text-zinc-600 group-hover:bg-zinc-200'
                                  }`}>
                                    Page {res.pageNumber}
                                  </span>
                                  {res.isOcr && (
                                    <span className="inline-flex items-center gap-1 text-[9px] font-mono font-extrabold bg-primary-50 text-primary-750 border border-primary-200/90 px-1.5 py-0.2 rounded-md shadow-2xs">
                                      <Sparkles className="w-2.5 h-2.5 text-primary-600" />
                                      Docarn AI
                                    </span>
                                  )}
                                </div>
                                <span className="text-[9px] font-mono text-zinc-400">
                                  #{idx + 1}
                                </span>
                              </div>
                              <p className="text-[11px] text-zinc-600 leading-relaxed font-sans line-clamp-3">
                                <span>{res.snippetBefore}</span>
                                <mark className="bg-primary-500/25 text-primary-950 font-black px-1 py-0.2 rounded border border-primary-500/30">
                                  {res.matchText}
                                </mark>
                                <span>{res.snippetAfter}</span>
                              </p>
                            </div>
                          );
                        })
                      ) : activeSearchTerm && !isSearching ? (
                        <div className="p-6 text-center space-y-2">
                          <AlertCircle className="w-7 h-7 text-zinc-300 mx-auto" />
                          <p className="text-xs font-semibold text-zinc-700">Aucune occurrence</p>
                          <p className="text-[11px] text-zinc-400">
                            Aucun passage ne correspond à « <strong className="text-zinc-600">{activeSearchTerm}</strong> ».
                          </p>
                        </div>
                      ) : (
                        <div className="p-4 space-y-4 text-center">
                          <div className="p-3.5 bg-primary-50/60 rounded-2xl border border-primary-100 text-center space-y-1.5">
                            <div className="w-7 h-7 rounded-xl bg-white text-primary-750 shadow-2xs border border-primary-200/60 flex items-center justify-center mx-auto mb-1">
                              <Sparkles className="w-4 h-4 text-primary-600" />
                            </div>
                            <p className="text-xs font-extrabold text-primary-950">Recherche Intelligente Docarn AI</p>
                            <p className="text-[11px] text-zinc-500 leading-relaxed">
                              Trouvez instantanément les informations essentielles et accédez en quelques secondes aux passages qui comptent vraiment.
                            </p>
                          </div>

                          <div className="space-y-1.5 text-left">
                            <span className="text-[10px] uppercase font-bold text-zinc-400 tracking-wider">
                              Suggestions rapides
                            </span>
                            <div className="flex flex-wrap gap-1.5">
                              {['Article', 'Décret', 'Société', 'Sanction', 'Obligation', 'Délai', 'Tribunal'].map(tag => (
                                <button
                                  key={tag}
                                  type="button"
                                  onClick={() => {
                                    setSearchQuery(tag);
                                    handleStartSearch(tag);
                                  }}
                                  className="text-[10px] font-semibold bg-zinc-100 hover:bg-primary-50 text-zinc-700 hover:text-primary-750 px-2.5 py-1 rounded-lg border border-zinc-200/80 hover:border-primary-200 transition cursor-pointer"
                                >
                                  {tag}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </aside>
            )}

            {/* Main Viewport Content Area (flex-1) */}
            <div className="flex-1 relative h-full overflow-hidden flex flex-col">
              {(!authUser) ? (
                <div className="absolute inset-0 bg-white/60 backdrop-blur-md flex flex-col items-center justify-center p-8 text-center space-y-4 z-10">
                  <div className="bg-white border border-zinc-200 p-6 rounded-3xl shadow-2xl max-w-md space-y-4">
                    <div className="bg-orange-light/10 border border-orange-light/30 text-orange-light p-3 rounded-full w-12 h-12 flex items-center justify-center mx-auto">
                      <Lock className="w-6 h-6" />
                    </div>
                    <h4 className="font-display text-base font-extrabold text-zinc-950">Accès au texte intégral restreint</h4>
                    <p className="text-xs text-zinc-550 leading-relaxed font-normal">
                      Le visualiseur PDF est bloqué car vous n'êtes pas connecté. Pour lire l'intégralité du texte de loi, veuillez vous connecter.
                    </p>
                    <button
                      onClick={() => openAuthModal()}
                      className="w-full py-3 bg-violet-medium hover:bg-violet-dark text-white font-bold text-xs uppercase tracking-widest rounded-xl transition-all cursor-pointer shadow-md shadow-violet-medium/15 border-none"
                    >
                      Se connecter / S'inscrire
                    </button>
                  </div>
                </div>
              ) : null}
              
              {/* Real PDF rendered as Canvas via react-pdf OR Fake Image for unauthenticated */}
              {!authUser ? (
                <div className="w-full h-full overflow-hidden bg-zinc-200 flex items-start justify-center">
                  <img 
                    src="/assets/Fake.PNG" 
                    alt="Texte flou" 
                    className="w-full max-w-3xl h-auto object-cover opacity-50 blur-[12px] grayscale pointer-events-none select-none"
                  />
                </div>
              ) : (activePageData || numPages) ? (
                <PdfViewerErrorBoundary onReset={() => {
                  pageDataCache.current.clear();
                  setActivePageData(null);
                }}>
                  <div 
                    ref={scrollContainerRef}
                    onScroll={handleScroll}
                    onWheel={handleWheel}
                    className="w-full h-full overflow-y-auto custom-scrollbar flex flex-col items-center gap-6 py-8 bg-zinc-200/50 relative"
                  >
                    {/* Badge d'occurrence sur la page active */}
                    {searchResults.length > 0 && searchResults.some(r => r.pageNumber === currentPage) && (
                      <div className="sticky top-2 z-30 bg-white/95 text-primary-750 border border-primary-200/90 px-3.5 py-1 rounded-full text-xs font-bold shadow-md backdrop-blur-md flex items-center gap-2 animate-fade-in">
                        <Sparkles className="w-3.5 h-3.5 text-primary-600" />
                        <span>Docarn AI • {searchResults.filter(r => r.pageNumber === currentPage).length} occurrence(s) sur la page {currentPage}</span>
                      </div>
                    )}

                    {viewMode === 'all' && numPages && numPages > 0 ? (
                      // Mode Défilement Continu Multi-Pages (Fluide, naturel, 60fps)
                      Array.from({ length: numPages }, (_, index) => {
                        const p = index + 1;
                        const isNear = Math.abs(p - currentPage) <= 2;
                        return (
                          <div
                            key={`page_container_${p}`}
                            ref={(el) => {
                              if (el) pageContainerRefs.current.set(p, el);
                              else pageContainerRefs.current.delete(p);
                            }}
                            data-page={p}
                            className="flex flex-col items-center select-none"
                          >
                            <VirtualPageItem
                              key={`vp_${p}_${ocrVersion}_${activeSearchTerm || 'none'}`}
                              pageNumber={p}
                              documentId={String(document.id)}
                              pdfZoom={pdfZoom}
                              isNearViewport={isNear}
                              isScrollingFast={isScrollingFast}
                              pageDataCache={pageDataCache}
                              activeSearchTerm={activeSearchTerm}
                              ocrData={extractedTextCache.current.get(p)}
                            />
                            <span className="text-[10px] font-mono mt-2 select-none text-zinc-500 font-medium">
                              Page {p} sur {numPages}
                            </span>
                          </div>
                        );
                      })
                    ) : (
                      // Mode Page par page (Tourne-page avec molette intelligente)
                      <div className="flex flex-col items-center select-none" onContextMenu={(e) => e.preventDefault()}>
                        {singlePageFileProp ? (
                          <Document 
                            file={singlePageFileProp} 
                            onLoadSuccess={onDocumentLoadSuccess}
                            onLoadError={(error) => console.error("React-PDF Load Error:", error)}
                            loading={
                              <div className="flex flex-col items-center justify-center p-12 rounded-xl shadow-lg border border-zinc-200 bg-white w-[500px] h-[700px] max-w-full animate-pulse select-none">
                                <FileText className="w-8 h-8 opacity-40 mb-2 animate-bounce text-zinc-400" />
                                <span className="text-xs font-semibold text-zinc-400">Chargement de la page {currentPage}...</span>
                              </div>
                            }
                            error={
                              <div className="w-full h-full flex flex-col items-center justify-center text-zinc-400 bg-zinc-50 p-6 text-center space-y-4">
                                <div className="bg-red-50 p-4 rounded-full">
                                  <AlertCircle className="w-10 h-10 text-red-400" />
                                </div>
                                <div>
                                  <h3 className="font-bold text-zinc-700 text-sm mb-1">Document introuvable ou corrompu</h3>
                                  <p className="text-xs text-zinc-500 max-w-sm">Le fichier physique associé à ce document n'a pas pu être chargé.</p>
                                </div>
                              </div>
                            }
                          >
                            <div className="relative inline-block select-none" onContextMenu={(e) => e.preventDefault()}>
                              <Page 
                                key={`single_page_${currentPage}_${ocrVersion}_${activeSearchTerm || 'none'}`} 
                                pageNumber={1} 
                                className="shadow-xl select-none pointer-events-none rounded-sm transition-all"
                                scale={pdfZoom / 100}
                                renderTextLayer={true}
                                renderAnnotationLayer={false}
                                customTextRenderer={singlePageCustomTextRenderer}
                              />
                              {/* Surlignage jaune Docarn AI (OCR) pour documents scannés en mode page unique */}
                              {singlePageMatchingOcrBoxes.length > 0 && currentPageOcrData?.canvasWidth && currentPageOcrData?.canvasHeight && (
                                <div className="absolute inset-0 pointer-events-none z-10 overflow-hidden">
                                  {singlePageMatchingOcrBoxes.map((w, idx) => (
                                    <div
                                      key={`single_ocr_hl_${currentPage}_${idx}`}
                                      className="docarn-ocr-highlight-box"
                                      style={{
                                        left: `${(w.bbox.x0 / currentPageOcrData.canvasWidth!) * 100}%`,
                                        top: `${(w.bbox.y0 / currentPageOcrData.canvasHeight!) * 100}%`,
                                        width: `${((w.bbox.x1 - w.bbox.x0) / currentPageOcrData.canvasWidth!) * 100}%`,
                                        height: `${((w.bbox.y1 - w.bbox.y0) / currentPageOcrData.canvasHeight!) * 100}%`,
                                      }}
                                      title={w.text}
                                    />
                                  ))}
                                </div>
                              )}
                            </div>
                          </Document>
                        ) : (
                          <div className="flex flex-col items-center justify-center p-12 rounded-xl shadow-lg border border-zinc-200 bg-white w-[500px] h-[700px] max-w-full animate-pulse select-none">
                            <FileText className="w-8 h-8 opacity-40 mb-2 animate-bounce text-zinc-400" />
                            <span className="text-xs font-semibold text-zinc-400">Chargement de la page {currentPage}...</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </PdfViewerErrorBoundary>
              ) : fileNotSupported ? (
                <div className="w-full h-full flex flex-col items-center justify-center text-zinc-400 bg-zinc-50 p-6 text-center space-y-4">
                  <div className="bg-blue-50 p-4 rounded-full">
                    <FileText className="w-10 h-10 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="font-bold text-zinc-700 text-sm mb-1">Prévisualisation non supportée</h3>
                    <p className="text-xs text-zinc-500 max-w-sm">Ce fichier n'est pas un document PDF. La prévisualisation en ligne n'est disponible que pour les PDF. Vous pouvez toutefois télécharger le fichier original.</p>
                  </div>
                </div>
              ) : pdfError ? (
                <div className="w-full h-full flex flex-col items-center justify-center text-zinc-400 bg-zinc-50 p-6 text-center space-y-4">
                  <div className="bg-red-50 p-4 rounded-full">
                    <AlertCircle className="w-10 h-10 text-red-400" />
                  </div>
                  <div>
                    <h3 className="font-bold text-zinc-700 text-sm mb-1">Fichier non disponible</h3>
                    <p className="text-xs text-zinc-500 max-w-sm">Le fichier source est introuvable sur le serveur sécurisé. Veuillez contacter l'administrateur ou vérifier plus tard.</p>
                  </div>
                </div>
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center bg-zinc-50 space-y-3">
                  <div className="w-8 h-8 border-2 border-violet-400 border-t-transparent rounded-full animate-spin"></div>
                  <div className="text-zinc-500 font-medium text-xs animate-pulse">Chargement sécurisé du document...</div>
                </div>
              )}
            </div>
          </div>
          </div>{/* /PDF Viewer wrapper */}

          {/* PRIVATE NOTES TEXTAREA BOX */}
          {!authUser ? (
            <div className="bg-white border border-zinc-200/80 rounded-2xl p-6 space-y-4 shadow-xs">
              <h4 className="font-bold text-xs text-zinc-900 uppercase tracking-wider flex items-center gap-1.5">
                <Lock className="w-3.5 h-3.5 text-zinc-500" /> Annotations professionnelles privées
              </h4>
              <p className="text-xs text-zinc-600 font-normal leading-relaxed">
                Connectez-vous pour rédiger des annotations confidentielles sur cet acte. Elles ne seront visibles que par vous.
              </p>
              <button
                onClick={() => openAuthModal()}
                className="w-full py-2.5 bg-zinc-900 hover:bg-zinc-800 text-white font-bold text-xs uppercase tracking-wider rounded-xl transition-all cursor-pointer shadow-xs border-none"
              >
                Se connecter / S'inscrire
              </button>
            </div>
          ) : (
            <div className="bg-white border border-zinc-200/80 rounded-2xl p-6 space-y-4 shadow-xs">
              <h4 className="font-bold text-xs text-zinc-900 uppercase tracking-wider flex items-center gap-1.5">
                <FileText className="w-3.5 h-3.5 text-primary-750" /> Notes professionnelles privées
              </h4>
              <p className="text-xs text-zinc-600 font-normal leading-relaxed">
                <span className="font-semibold text-zinc-800">Ces annotations sont strictement confidentielles et réservées à votre compte.</span> Elles sont sauvegardées sous l'acte.
              </p>

              <textarea
                rows={4}
                maxLength={500}
                disabled={noteLoading}
                placeholder="Rédigez vos observations, jurisprudence liée ou commentaires juridiques..."
                value={noteContent}
                onChange={(e) => setNoteContent(e.target.value)}
                className="w-full text-xs p-3.5 bg-zinc-50/50 border border-zinc-200 rounded-xl focus:outline-none focus:border-zinc-400 focus:bg-white text-zinc-800 leading-relaxed placeholder-zinc-400 disabled:opacity-60 transition"
              ></textarea>

              <div className="flex justify-between items-center pt-1.5">
                <span className={`text-[10px] font-semibold flex items-center gap-1 ${noteContent.length > 500 ? 'text-red-500' : 'text-zinc-400'}`}>
                  {noteContent.length}/500 caractères
                </span>

                <button
                  type="button"
                  onClick={handleSaveNote}
                  disabled={noteSaving || noteContent.length > 500 || !noteContent.trim()}
                  className="bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs uppercase px-4 py-2.5 rounded-xl transition-colors flex items-center gap-1.5 cursor-pointer focus:outline-none border-none shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {noteSaving ? (
                    <>Enregistrement...</>
                  ) : (
                    <><Save className="w-3.5 h-3.5" /> Enregistrer la note</>
                  )}
                </button>
              </div>

              {noteSavedFeedback && (
                <div className="bg-emerald-50 border border-emerald-200 px-3 py-2 text-emerald-800 text-[11px] font-semibold rounded-xl flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Note privée enregistrée avec succès
                </div>
              )}

              {documentNotes.length > 0 && (
                <div className="space-y-3 pt-2">
                  {documentNotes.map((note) => (
                    <div key={note.id} className="bg-white border border-amber-200/60 rounded-xl p-4 space-y-2">
                      <div className="flex items-start justify-between gap-3">
                        <p className="text-xs text-zinc-700 whitespace-pre-wrap leading-relaxed font-medium flex-1">
                          {note.content}
                        </p>
                        <button
                          onClick={() => note.id && handleDeleteNote(note.id)}
                          className="text-zinc-400 hover:text-red-500 transition-colors p-1"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <span className="text-[10px] text-zinc-400 font-semibold flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {formatFrenchDate(note.updatedAt)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* SIMILAR SUGGESTED READ LINKS */}
          <div className="space-y-4 pt-4">
            {/* <h4 className="font-display font-bold text-base text-zinc-900 border-b border-zinc-100 pb-2.5">
              Autres textes similaires recommandés
            </h4> */}
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {similarTexts.map((simDoc) => (
                <div
                  key={simDoc.id}
                  onClick={() => setActiveDocumentId(simDoc.id)}
                  className="bg-white p-5 border border-zinc-200 hover:border-primary-750/20 rounded-2xl hover:shadow-md transition-all cursor-pointer flex flex-col justify-between gap-3 text-zinc-900 group"
                >
                  <div className="space-y-2">
                    <span className="text-[9px] font-mono font-bold bg-primary-50 text-primary-750 border border-primary-100/40 px-2 py-0.5 rounded-full uppercase tracking-wide">
                      {simDoc.nature}
                    </span>
                    <h5 className="font-bold text-xs leading-snug line-clamp-2 text-zinc-800 group-hover:text-primary-750 transition-colors">
                      {simDoc.officialTitle}
                    </h5>
                  </div>
                  <span className="text-[10px] text-zinc-400 font-semibold block">Adopté en : {simDoc.effectiveYear}</span>
                </div>
              ))}
            </div>
          </div>

        </div>

      </div>

      {/* Folder Assignment Modal */}
      {document && (
        <FolderModal
          isOpen={isFolderModalOpen}
          onClose={() => setIsFolderModalOpen(false)}
          documentId={document.id}
        />
      )}

      {/* Paywall Consultation Limit Reached Modal */}
      {isConsultationLimitReached && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm"></div>
          <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-lg p-6 sm:p-8 text-center animate-fade-in border border-slate-100">
            <div className="w-16 h-16 bg-amber-50 rounded-2xl flex items-center justify-center mx-auto mb-4 text-amber-600 border border-amber-200 shadow-xs">
              <Sparkles className="w-8 h-8" />
            </div>

            <h3 className="text-xl font-bold text-slate-900 mb-2">
              Limite de Consultation Atteinte
            </h3>

            <p className="text-xs sm:text-sm text-slate-600 leading-relaxed mb-6">
              Vous avez atteint votre quota de consultation d'actes juridiques. Pour continuer à feuilleter le fonds documentaire et bénéficier des téléchargements officiels, découvrez nos formules d'abonnement.
            </p>

            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 text-left text-xs text-slate-600 space-y-2 mb-6">
              <div className="font-bold text-violet-900 flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4 text-violet-700" />
                <span>Avec une formule supérieure :</span>
              </div>
              <ul className="space-y-1 text-slate-600 pl-5 list-disc">
                <li>Consultation intégrale et illimitée de tous les textes</li>
                <li>Téléchargement certifié des documents physiques PDF</li>
                <li>Recherche sémantique avancée et dossiers thématiques</li>
              </ul>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-3">
              <button
                type="button"
                onClick={() => navigate('/formules')}
                className="w-full sm:flex-1 py-3 px-5 bg-violet-700 hover:bg-violet-800 active:bg-violet-900 text-white font-bold text-xs rounded-xl shadow-md shadow-violet-700/20 transition-all flex items-center justify-center gap-2 cursor-pointer"
              >
                <span>Découvrir les formules</span>
                <ChevronRight className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => navigate('/search')}
                className="w-full sm:w-auto py-3 px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold text-xs rounded-xl transition-colors cursor-pointer"
              >
                Retour à la recherche
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
